/**
 * render/title.ts — The Title Screen
 *
 * You are floating in infinite dark. Far away, a warmth — a single point
 * of golden light. It grows. Around it, a room materializes — wood and stone
 * and firelight. A cat curled on a rug. An armchair waiting. Embers rise from
 * the hearth and become stars.
 *
 * This is the first breath. This is the invitation.
 *
 * The cosmos is not a backdrop — it breathes. Aurora veils drift across
 * the sky. Constellations trace themselves and dissolve. The fire is not
 * an animation — it dances with pointed tongues and casts shadows that
 * sway on the walls. Everything responds to everything.
 *
 * This is not a loading screen. This is the threshold between the cold
 * infinite and the warm intimate. The entire thesis of Hearth, in one scene.
 */

import { GameState } from "../engine/state.js";

// ─── Palette ─────────────────────────────────────────────────────────────────

const P = {
  void:        "#030308",
  deepIndigo:  "#08061a",
  nebulaPurp:  "#12082a",
  nebulaBlue:  "#0a1028",
  nebulaTeal:  "#081820",
  nebulaRose:  "#180818",

  floorA:      "#5c3d1e",
  floorB:      "#4a3018",
  wallNorth:   "#8a7a6a",
  wallWest:    "#6a5a4a",
  wallLine:    "rgba(0,0,0,0.12)",

  stone:       "#7a6a5a",
  stoneDark:   "#5a4a3a",
  stoneLight:  "#9a8a7a",
  hearth:      "#1a0a04",

  woodLight:   "#c4956a",
  woodMid:     "#8a6a40",
  woodDark:    "#6a4a2a",
  rugBase:     "#8a3028",
  rugBorder:   "#c47838",
  rugInner:    "#d4985a",
  cushion:     "#b86830",
  cushionHi:   "#d08040",

  chair:       "#7a5a3a",
  chairDark:   "#5a3a1a",
  chairSeat:   "#8a6a4a",

  cat:         "#2a2a30",

  aeternaBody: "#0d0d1e",

  titleWarm:   "#f0d8a0",
  titleGlow:   "#d4854a",
  sub:         "#b8a078",
  prompt:      "#c8b088",
} as const;

// ─── Iso helpers ─────────────────────────────────────────────────────────────

const TW = 32;
const TH = 16;

function iso(wx: number, wy: number, wz: number = 0): { x: number; y: number } {
  return { x: (wx - wy) * TW, y: (wx + wy) * TH - wz * 16 };
}

// ─── Data types ──────────────────────────────────────────────────────────────

interface Star { x: number; y: number; s: number; b: number; ph: number; layer: number }
interface Ember { x: number; y: number; vx: number; vy: number; life: number; max: number; s: number; hue: number }
interface Mote { x: number; y: number; vx: number; vy: number; life: number; max: number; s: number }
interface Wisp { x: number; y: number; age: number; maxAge: number; drift: number; amp: number; freq: number; size: number }
interface Spark { x: number; y: number; vx: number; vy: number; life: number; max: number; s: number }

// ─── Skeletal animal system ──────────────────────────────────────────────────
// Joint-based: every part is an offset from its parent. Animations transform
// joint angles/positions; rendering walks the skeleton tree. This approach
// ensures nothing floats or disconnects, and works for any quadruped.

type CatState = "sleep" | "alert" | "sit" | "walk" | "scratch" | "yawn" | "groom";

/** Pose: a snapshot of all joint angles and blendable properties.
 *  Values are normalized (-1..1 or 0..1) and interpolated between states. */
interface CatPose {
  spineAngle: number;   // 0=flat (sleeping), 0.5=angled (sitting), 1=upright
  headLift: number;     // 0=resting on paws, 1=fully raised
  headTurn: number;     // -1=left, 0=center, 1=right
  jawOpen: number;      // 0=closed, 1=yawning
  earLTwitch: number;   // -1..1 ear deflection
  earRTwitch: number;
  tailCurl: number;     // base curl amount
  tailWag: number;      // oscillation amplitude
  frontLegLPhase: number; // 0..1 walk cycle phase
  frontLegRPhase: number;
  backLegLPhase: number;
  backLegRPhase: number;
  scratchLeg: number;   // 0=none, 1=full scratch motion
  eyeOpen: number;      // 0=closed, 1=open
  walkX: number;        // world-space offset from home
}

interface CatAnim {
  state: CatState;
  stateT: number;
  nextChange: number;
  pose: CatPose;       // current interpolated pose
  target: CatPose;     // target pose for current state
  walkDir: number;     // -1 or 1
}

interface Moth {
  angle: number;     // orbital angle around candle
  radius: number;    // distance from candle
  wingPhase: number; // wing flap cycle
  active: boolean;
}

// ─── Constellation patterns (normalized 0-1 positions) ──────────────────────

const CONSTELLATIONS: { x: number; y: number }[][] = [
  [{ x: 0.14, y: 0.11 }, { x: 0.21, y: 0.07 }, { x: 0.19, y: 0.15 }],
  [{ x: 0.72, y: 0.14 }, { x: 0.78, y: 0.10 }, { x: 0.83, y: 0.14 }, { x: 0.78, y: 0.18 }],
  [{ x: 0.42, y: 0.05 }, { x: 0.47, y: 0.09 }, { x: 0.51, y: 0.06 }, { x: 0.55, y: 0.10 }],
];

// ═════════════════════════════════════════════════════════════════════════════
//  TITLE RENDERER
// ═════════════════════════════════════════════════════════════════════════════

export class TitleRenderer {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private stars: Star[] = [];
  private embers: Ember[] = [];
  private dustMotes: Mote[] = [];
  private smokeWisps: Wisp[] = [];
  private steamWisps: Wisp[] = [];
  private sparks: Spark[] = [];
  private t0: number | null = null;

  // Room transform — computed each frame
  private ox = 0;
  private oy = 0;
  private sc = 1;

  // Mouse parallax (normalized 0-1)
  private mx = 0.5;
  private my = 0.5;
  private readonly _onMouse: (e: MouseEvent) => void;

  // Cat animation — skeletal pose system
  private static readonly CAT_ZERO_POSE: CatPose = {
    spineAngle: 0, headLift: 0, headTurn: 0, jawOpen: 0,
    earLTwitch: 0, earRTwitch: 0, tailCurl: 0.3, tailWag: 0,
    frontLegLPhase: 0, frontLegRPhase: 0, backLegLPhase: 0, backLegRPhase: 0,
    scratchLeg: 0, eyeOpen: 0, walkX: 0,
  };
  private catAnim: CatAnim = {
    state: "sleep", stateT: 0, nextChange: 8 + Math.random() * 12, walkDir: 1,
    pose:   { ...TitleRenderer.CAT_ZERO_POSE },
    target: { ...TitleRenderer.CAT_ZERO_POSE },
  };

  // Moth — appears after ~180s as easter egg
  private moth: Moth = { angle: 0, radius: 12, wingPhase: 0, active: false };

  // Easter egg tracking
  private easterEggsTriggered: Set<string> = new Set();
  private nextSparkBurst = 75 + Math.random() * 30;  // first spark shower
  private grandShootingStar = false;
  private auroraBoost = 0; // 0-1, boosted during aurora surge easter egg

  constructor(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) {
    this.canvas = canvas;
    this.ctx = ctx;
    this._seedStars();

    this._onMouse = (e: MouseEvent) => {
      const r = this.canvas.getBoundingClientRect();
      this.mx = (e.clientX - r.left) / r.width;
      this.my = (e.clientY - r.top) / r.height;
    };
    canvas.addEventListener("mousemove", this._onMouse, { passive: true });
  }

  private _seedStars(): void {
    for (let i = 0; i < 260; i++) {
      const layer = i < 70 ? 0 : i < 170 ? 1 : 2;
      this.stars.push({
        x: Math.random(), y: Math.random(),
        s: layer === 0 ? 0.3 + Math.random() * 0.5 : layer === 1 ? 0.5 + Math.random() * 0.9 : 0.9 + Math.random() * 1.6,
        b: 0.25 + Math.random() * 0.75,
        ph: Math.random() * Math.PI * 2,
        layer,
      });
    }
  }

  // ─── Helpers ───────────────────────────────────────────────────────────────

  private w2s(wx: number, wy: number, wz: number = 0): { x: number; y: number } {
    const p = iso(wx, wy, wz);
    return { x: this.ox + p.x * this.sc, y: this.oy + p.y * this.sc };
  }

  private quad(
    ctx: CanvasRenderingContext2D,
    x1: number, y1: number, z1: number,
    x2: number, y2: number, z2: number,
    x3: number, y3: number, z3: number,
    x4: number, y4: number, z4: number,
    fill: string, stroke?: string
  ): void {
    const a = this.w2s(x1, y1, z1), b = this.w2s(x2, y2, z2);
    const c = this.w2s(x3, y3, z3), d = this.w2s(x4, y4, z4);
    ctx.beginPath();
    ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y);
    ctx.lineTo(c.x, c.y); ctx.lineTo(d.x, d.y);
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
    if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = 0.5; ctx.stroke(); }
  }

  // ═════════════════════════════════════════════════════════════════════════════
  //  RENDER — called every frame
  // ═════════════════════════════════════════════════════════════════════════════

  render(timestamp: number, state: GameState, quality: number): void {
    if (this.t0 === null) this.t0 = timestamp;
    const t = (timestamp - this.t0) / 1000;
    const ctx = this.ctx;
    const W = state.canvasWidth;
    const H = state.canvasHeight;

    // ── Room transform: center the visual extent of the room ────────────
    const naturalExtent = 180;
    this.sc = Math.min((H * 0.52) / naturalExtent, W * 0.003);
    this.ox = W * 0.5;
    this.oy = H * 0.48 - 54 * this.sc;

    // ── Cinematic timing ────────────────────────────────────────────────
    const tCosmos = Math.min(1, t / 1.2);
    const tRoom   = Math.max(0, Math.min(1, (t - 0.8) / 1.5));
    const tTitle  = Math.max(0, Math.min(1, (t - 2.2) / 1.4));
    const tSub    = Math.max(0, Math.min(1, (t - 3.4) / 0.8));
    const tPrompt = Math.max(0, Math.min(1, (t - 4.2) / 0.6));

    // ── Easter eggs — timed events for patient watchers ────────────────
    this._checkEasterEggs(t);

    // ── Layers ──────────────────────────────────────────────────────────
    this._drawCosmos(ctx, t, W, H, quality, tCosmos);
    this._drawWarmthBleed(ctx, t, W, H, tRoom);
    if (tRoom > 0) {
      ctx.save();
      ctx.globalAlpha = tRoom;
      this._drawRoom(ctx, t, W, H, quality);
      this._drawFireLighting(ctx, t, quality);
      if (this.moth.active) this._drawMoth(ctx, t);
      ctx.restore();
    }
    this._drawParticles(ctx, t, W, H, quality, tRoom);
    this._drawOverlay(ctx, t, W, H, tCosmos, tTitle, tSub, tPrompt);
  }

  // ═════════════════════════════════════════════════════════════════════════════
  //  LAYER 1 — THE COSMOS
  // ═════════════════════════════════════════════════════════════════════════════

  private _drawCosmos(ctx: CanvasRenderingContext2D, t: number, W: number, H: number, quality: number, reveal: number): void {
    // Deep space gradient — asymmetric for depth
    const bg = ctx.createRadialGradient(W * 0.35, H * 0.28, 0, W * 0.5, H * 0.5, Math.max(W, H) * 0.82);
    bg.addColorStop(0,   P.nebulaBlue);
    bg.addColorStop(0.25, P.nebulaPurp);
    bg.addColorStop(0.55, P.deepIndigo);
    bg.addColorStop(1,   P.void);
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    if (reveal < 0.1) return;

    // ── Nebula clouds — slow-orbiting color veils ───────────────────────
    if (quality >= 0.5) {
      const nebulae = [
        { cx: W * (0.28 + 0.12 * Math.sin(t * 0.025)), cy: H * (0.22 + 0.08 * Math.cos(t * 0.02)),
          r: W * 0.38, color: "rgba(40, 20, 90, 0.07)" },
        { cx: W * (0.72 + 0.10 * Math.sin(t * 0.03 + 2)), cy: H * (0.18 + 0.06 * Math.cos(t * 0.022 + 1)),
          r: W * 0.32, color: "rgba(15, 35, 70, 0.06)" },
        { cx: W * (0.55 + 0.08 * Math.sin(t * 0.018 + 4)), cy: H * (0.65 + 0.05 * Math.cos(t * 0.028 + 3)),
          r: W * 0.30, color: "rgba(10, 40, 50, 0.05)" },
        { cx: W * (0.20 + 0.06 * Math.sin(t * 0.035 + 5)), cy: H * (0.55 + 0.07 * Math.cos(t * 0.019)),
          r: W * 0.25, color: "rgba(50, 15, 40, 0.04)" },
      ];
      if (quality >= 0.7) {
        nebulae.push({
          cx: W * (0.50 + 0.15 * Math.sin(t * 0.015 + 6)), cy: H * (0.35 + 0.10 * Math.cos(t * 0.012 + 2)),
          r: W * 0.45, color: "rgba(25, 10, 60, 0.035)"
        });
      }
      for (const n of nebulae) {
        const g = ctx.createRadialGradient(n.cx, n.cy, 0, n.cx, n.cy, n.r);
        g.addColorStop(0, n.color);
        g.addColorStop(0.6, n.color.replace(/[\d.]+\)$/, "0.02)"));
        g.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, W, H);
      }
    }

    // ── Aurora veil — sine-wave ribbons ──────────────────────────────────
    if (quality >= 0.6) {
      ctx.save();
      const auroraAlpha = (0.035 + 0.02 * Math.sin(t * 0.25) + this.auroraBoost * 0.15) * reveal;
      ctx.globalAlpha = auroraAlpha;
      const ag = ctx.createLinearGradient(0, H * 0.12, 0, H * 0.32);
      ag.addColorStop(0,   "rgba(20, 100, 70, 0)");
      ag.addColorStop(0.25, "rgba(30, 140, 90, 0.7)");
      ag.addColorStop(0.5, "rgba(60, 90, 160, 0.9)");
      ag.addColorStop(0.75, "rgba(90, 50, 130, 0.6)");
      ag.addColorStop(1,   "rgba(50, 30, 70, 0)");
      ctx.fillStyle = ag;
      ctx.beginPath();
      ctx.moveTo(-10, H * 0.30);
      for (let x = -10; x <= W + 10; x += 8) {
        const y = H * 0.20 + Math.sin(x * 0.003 + t * 0.18) * H * 0.025
                  + Math.sin(x * 0.0065 - t * 0.13) * H * 0.012;
        ctx.lineTo(x, y);
      }
      for (let x = W + 10; x >= -10; x -= 8) {
        const y = H * 0.30 + Math.sin(x * 0.004 - t * 0.15) * H * 0.018
                  + Math.sin(x * 0.008 + t * 0.10) * H * 0.008;
        ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill();

      // Second aurora band — thinner, offset
      if (quality >= 0.8) {
        ctx.globalAlpha = auroraAlpha * 0.6;
        const ag2 = ctx.createLinearGradient(0, H * 0.08, 0, H * 0.22);
        ag2.addColorStop(0,   "rgba(80, 40, 120, 0)");
        ag2.addColorStop(0.4, "rgba(60, 80, 160, 0.5)");
        ag2.addColorStop(0.7, "rgba(40, 120, 100, 0.4)");
        ag2.addColorStop(1,   "rgba(20, 60, 60, 0)");
        ctx.fillStyle = ag2;
        ctx.beginPath();
        ctx.moveTo(-10, H * 0.22);
        for (let x = -10; x <= W + 10; x += 10) {
          const y = H * 0.14 + Math.sin(x * 0.0025 + t * 0.22 + 1.5) * H * 0.018
                    + Math.sin(x * 0.007 - t * 0.09 + 0.5) * H * 0.008;
          ctx.lineTo(x, y);
        }
        for (let x = W + 10; x >= -10; x -= 10) {
          ctx.lineTo(x, H * 0.20 + Math.sin(x * 0.003 + t * 0.12 + 2) * H * 0.01);
        }
        ctx.closePath();
        ctx.fill();
      }
      ctx.restore();
    }

    // ── Stars — 3 parallax layers with mouse response ───────────────────
    const px = (this.mx - 0.5);
    const py = (this.my - 0.5);
    for (const star of this.stars) {
      const drift = star.layer === 0 ? 0.002 : star.layer === 1 ? 0.005 : 0.012;
      const parallax = star.layer === 0 ? 4 : star.layer === 1 ? 10 : 20;

      const sx = (((star.x + t * drift * 0.3) % 1 + 1) % 1) * W - px * parallax;
      const sy = star.y * H - py * parallax * 0.7;

      const twinkle = 0.4 + 0.6 * Math.sin(t * (1.2 + star.layer * 0.7) + star.ph);
      const alpha = star.b * twinkle * reveal;
      if (alpha < 0.04) continue;

      ctx.globalAlpha = alpha;
      const warm = star.ph > Math.PI;
      ctx.fillStyle = warm ? "#ffe8cc" : "#cce0ff";
      ctx.beginPath();
      ctx.arc(sx, sy, star.s, 0, Math.PI * 2);
      ctx.fill();

      // Bright stars get tiny diffraction cross
      if (star.layer === 2 && star.b > 0.7 && twinkle > 0.7) {
        ctx.strokeStyle = warm ? "rgba(255,230,200,0.25)" : "rgba(200,220,255,0.25)";
        ctx.lineWidth = 0.5;
        const spikeLen = star.s * 3;
        ctx.beginPath();
        ctx.moveTo(sx - spikeLen, sy); ctx.lineTo(sx + spikeLen, sy);
        ctx.moveTo(sx, sy - spikeLen); ctx.lineTo(sx, sy + spikeLen);
        ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;

    // ── Constellation traces — ghost lines that breathe ─────────────────
    if (quality >= 0.6) {
      for (let ci = 0; ci < CONSTELLATIONS.length; ci++) {
        const pts = CONSTELLATIONS[ci];
        const cycle = 35;
        const phase = ci * 12;
        const cAlpha = Math.max(0, Math.sin(((t + phase) / cycle) * Math.PI * 2)) * 0.12 * reveal;
        if (cAlpha < 0.01) continue;

        ctx.globalAlpha = cAlpha * 3;
        ctx.fillStyle = "#e0d8f0";
        for (const pt of pts) {
          ctx.beginPath();
          ctx.arc(pt.x * W - px * 6, pt.y * H - py * 4, 1.2, 0, Math.PI * 2);
          ctx.fill();
        }

        ctx.globalAlpha = cAlpha;
        ctx.strokeStyle = "#8080b0";
        ctx.lineWidth = 0.6;
        ctx.beginPath();
        ctx.moveTo(pts[0].x * W - px * 6, pts[0].y * H - py * 4);
        for (let i = 1; i < pts.length; i++) {
          ctx.lineTo(pts[i].x * W - px * 6, pts[i].y * H - py * 4);
        }
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    // ── Shooting star ───────────────────────────────────────────────────
    if (quality >= 0.7) {
      const period = 7.3;
      const sp = ((t * 0.137) % 1);
      if (sp < 0.025) {
        const p = sp / 0.025;
        const a = Math.sin(p * Math.PI);
        const startX = W * (0.15 + (Math.floor(t / period) * 0.37 % 0.6));
        const startY = H * (0.05 + (Math.floor(t / period) * 0.23 % 0.2));
        const sx = startX + p * W * 0.25;
        const sy = startY + p * H * 0.08;
        ctx.strokeStyle = `rgba(255, 252, 245, ${a * 0.8})`;
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(sx - 50 * a, sy - 12 * a);
        ctx.stroke();
        ctx.fillStyle = `rgba(255, 240, 220, ${a * 0.3})`;
        ctx.beginPath();
        ctx.arc(sx - 25 * a, sy - 6 * a, 1, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // ── Grand shooting star — easter egg at ~55s ──────────────────────
    if (this.grandShootingStar) {
      const gt = ((t - 55) % 3) / 2.5;
      if (gt >= 0 && gt < 1) {
        const ga = Math.sin(gt * Math.PI);
        const gsx = W * 0.1 + gt * W * 0.7;
        const gsy = H * 0.06 + gt * H * 0.12;
        // Main streak — bright and wide
        ctx.strokeStyle = `rgba(255, 252, 240, ${ga * 0.95})`;
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.moveTo(gsx, gsy);
        ctx.lineTo(gsx - 120 * ga, gsy - 20 * ga);
        ctx.stroke();
        // Glow trail
        ctx.strokeStyle = `rgba(255, 200, 100, ${ga * 0.4})`;
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.moveTo(gsx - 30, gsy - 5);
        ctx.lineTo(gsx - 200 * ga, gsy - 30 * ga);
        ctx.stroke();
        // Scatter sparkles along trail
        for (let sp = 0; sp < 6; sp++) {
          const sf = sp / 6;
          const spx = gsx - sf * 160 * ga + (Math.random() - 0.5) * 8;
          const spy = gsy - sf * 25 * ga + (Math.random() - 0.5) * 6;
          ctx.fillStyle = `rgba(255, 240, 200, ${ga * (1 - sf) * 0.5})`;
          ctx.beginPath();
          ctx.arc(spx, spy, 0.5 + (1 - sf) * 1.5, 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
  }

  // ═════════════════════════════════════════════════════════════════════════════
  //  WARMTH BLEED — fire glow reaching into the cosmos
  // ═════════════════════════════════════════════════════════════════════════════

  private _drawWarmthBleed(ctx: CanvasRenderingContext2D, t: number, W: number, H: number, roomReveal: number): void {
    if (roomReveal < 0.01) return;
    const fireScreen = this.w2s(0.8, 0.6, 0.5);
    const flicker = 0.7 + 0.3 * Math.sin(t * 3.2);
    const radius = Math.max(W, H) * (0.25 + roomReveal * 0.2);
    const alpha = roomReveal * 0.12 * flicker;

    const g = ctx.createRadialGradient(fireScreen.x, fireScreen.y, 0, fireScreen.x, fireScreen.y, radius);
    g.addColorStop(0,   `rgba(255, 140, 50, ${alpha})`);
    g.addColorStop(0.3, `rgba(200, 80, 20, ${alpha * 0.5})`);
    g.addColorStop(0.6, `rgba(120, 40, 10, ${alpha * 0.15})`);
    g.addColorStop(1,   "rgba(80, 20, 0, 0)");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  }

  // ═════════════════════════════════════════════════════════════════════════════
  //  LAYER 2 — THE ROOM
  // ═════════════════════════════════════════════════════════════════════════════

  private _drawRoom(ctx: CanvasRenderingContext2D, t: number, _W: number, _H: number, quality: number): void {
    this._drawFloor(ctx, t);
    this._drawWalls(ctx);
    this._drawWindow(ctx, t, quality);
    this._drawFireReflectionOnWindow(ctx, t);
    this._drawPictureFrame(ctx);
    this._drawFireplace(ctx, t, quality);
    this._drawBookcase(ctx);
    this._drawFurnitureShadows(ctx, t);
    this._drawRug(ctx);
    this._drawArmchair(ctx, t);
    this._drawCat(ctx, t);
    this._drawTable(ctx);
    this._drawAeterna(ctx, t);
  }

  // ── Floor ──────────────────────────────────────────────────────────────────

  private _drawFloor(ctx: CanvasRenderingContext2D, t: number): void {
    // Warm tint strength from fire
    const fireFk = 0.7 + 0.3 * Math.sin(t * 3.5);

    for (let y = 0; y < 4; y++) {
      for (let x = 0; x < 5; x++) {
        const shade = (x + y) % 2 === 0 ? P.floorA : P.floorB;
        this.quad(ctx, x, y, 0, x + 1, y, 0, x + 1, y + 1, 0, x, y + 1, 0, shade, P.wallLine);

        // Warm tint on tiles near the fire (x=0..2, y=0..1)
        const dist = Math.sqrt((x + 0.5 - 0.8) ** 2 + (y + 0.5) ** 2);
        if (dist < 2.5) {
          const warmAlpha = Math.max(0, (1 - dist / 2.5)) * 0.12 * fireFk;
          this.quad(ctx, x, y, 0.001, x + 1, y, 0.001, x + 1, y + 1, 0.001, x, y + 1, 0.001,
            `rgba(255, 120, 40, ${warmAlpha})`);
        }

        if (this.sc > 1.2) {
          const c = this.w2s(x + 0.5, y + 0.5, 0);
          ctx.strokeStyle = "rgba(0,0,0,0.05)";
          ctx.lineWidth = 0.3;
          for (let g = 0; g < 3; g++) {
            const gy = c.y + (g - 1) * 3.5 * this.sc;
            ctx.beginPath();
            ctx.moveTo(c.x - 8 * this.sc, gy);
            ctx.lineTo(c.x + 8 * this.sc, gy + 0.5);
            ctx.stroke();
          }
        }
      }
    }
  }

  // ── Walls ──────────────────────────────────────────────────────────────────

  private _drawWalls(ctx: CanvasRenderingContext2D): void {
    const wallH = 2.2;
    const es = this.sc;

    // North wall panels
    for (let x = 0; x < 5; x++) {
      this.quad(ctx, x, 0, wallH, x + 1, 0, wallH, x + 1, 0, 0, x, 0, 0, P.wallNorth, P.wallLine);
    }
    // West wall panels
    for (let y = 0; y < 4; y++) {
      this.quad(ctx, 0, y, wallH, 0, y + 1, wallH, 0, y + 1, 0, 0, y, 0, P.wallWest, P.wallLine);
    }

    // Wainscoting line — chair rail at z=0.7 on both walls
    const railZ = 0.7;
    // North wall rail
    const nrl = this.w2s(0, 0, railZ), nrr = this.w2s(5, 0, railZ);
    ctx.strokeStyle = "rgba(120, 100, 80, 0.25)";
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(nrl.x, nrl.y); ctx.lineTo(nrr.x, nrr.y); ctx.stroke();
    // West wall rail
    const wrt = this.w2s(0, 0, railZ), wrb = this.w2s(0, 4, railZ);
    ctx.beginPath(); ctx.moveTo(wrt.x, wrt.y); ctx.lineTo(wrb.x, wrb.y); ctx.stroke();

    // Baseboard — thin dark strip at floor-wall junction
    ctx.strokeStyle = "rgba(60, 45, 25, 0.35)";
    ctx.lineWidth = 1.5;
    const bbl = this.w2s(0, 0, 0.03), bbr = this.w2s(5, 0, 0.03);
    ctx.beginPath(); ctx.moveTo(bbl.x, bbl.y); ctx.lineTo(bbr.x, bbr.y); ctx.stroke();
    const wbl = this.w2s(0, 0, 0.03), wbr = this.w2s(0, 4, 0.03);
    ctx.beginPath(); ctx.moveTo(wbl.x, wbl.y); ctx.lineTo(wbr.x, wbr.y); ctx.stroke();

    // Crown moulding hint at ceiling
    const crownZ = wallH - 0.03;
    ctx.strokeStyle = "rgba(160, 140, 120, 0.18)";
    ctx.lineWidth = 0.8;
    const cml = this.w2s(0, 0, crownZ), cmr = this.w2s(5, 0, crownZ);
    ctx.beginPath(); ctx.moveTo(cml.x, cml.y); ctx.lineTo(cmr.x, cmr.y); ctx.stroke();
    const cwl = this.w2s(0, 0, crownZ), cwr = this.w2s(0, 4, crownZ);
    ctx.beginPath(); ctx.moveTo(cwl.x, cwl.y); ctx.lineTo(cwr.x, cwr.y); ctx.stroke();

    // Subtle plaster texture on north wall — faint vertical streaks
    if (es > 0.8) {
      ctx.globalAlpha = 0.025;
      ctx.strokeStyle = "#a09888";
      ctx.lineWidth = 0.3;
      for (let i = 0; i < 8; i++) {
        const wx = 0.3 + i * 0.58;
        const a = this.w2s(wx, 0, 0.08), b = this.w2s(wx + 0.02, 0, wallH - 0.1);
        ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }
  }

  // ── Window ─────────────────────────────────────────────────────────────────

  private _drawWindow(ctx: CanvasRenderingContext2D, t: number, quality: number): void {
    const wx = 2.8, wBot = 0.5, wTop = 1.8, wW = 0.9;
    const bl = this.w2s(wx, 0, wBot), br = this.w2s(wx + wW, 0, wBot);
    const tr = this.w2s(wx + wW, 0, wTop), tl = this.w2s(wx, 0, wTop);

    // Night sky
    ctx.beginPath();
    ctx.moveTo(tl.x, tl.y); ctx.lineTo(tr.x, tr.y);
    ctx.lineTo(br.x, br.y); ctx.lineTo(bl.x, bl.y);
    ctx.closePath();
    ctx.fillStyle = "#060612";
    ctx.fill();

    // Window stars
    if (quality > 0.5) {
      ctx.save();
      ctx.beginPath();
      ctx.moveTo(tl.x, tl.y); ctx.lineTo(tr.x, tr.y);
      ctx.lineTo(br.x, br.y); ctx.lineTo(bl.x, bl.y);
      ctx.clip();
      for (let i = 0; i < 10; i++) {
        const frac = ((i * 0.131 + 0.06) % 1);
        const fracy = ((i * 0.179 + 0.12) % 1);
        const lx = bl.x + (tr.x - bl.x) * frac + (br.x - bl.x) * fracy * 0.3;
        const ly = tl.y + (bl.y - tl.y) * fracy;
        const tw = 0.5 + 0.5 * Math.sin(t * 2.1 + i * 1.9);
        ctx.globalAlpha = 0.3 + 0.5 * tw;
        ctx.fillStyle = "#dde4ff";
        ctx.beginPath();
        ctx.arc(lx, ly, 0.5 + tw * 0.3, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
      ctx.restore();
    }

    // Frame
    ctx.strokeStyle = "#c8a870";
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(tl.x, tl.y); ctx.lineTo(tr.x, tr.y);
    ctx.lineTo(br.x, br.y); ctx.lineTo(bl.x, bl.y);
    ctx.closePath();
    ctx.stroke();

    // Cross dividers
    const midZ = (wBot + wTop) / 2;
    const ml = this.w2s(wx, 0, midZ), mr = this.w2s(wx + wW, 0, midZ);
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(ml.x, ml.y); ctx.lineTo(mr.x, mr.y); ctx.stroke();
    const mv = this.w2s(wx + wW / 2, 0, wBot), mt = this.w2s(wx + wW / 2, 0, wTop);
    ctx.beginPath(); ctx.moveTo(mv.x, mv.y); ctx.lineTo(mt.x, mt.y); ctx.stroke();

    // Window sill — a thin ledge with depth
    const sillDepth = 0.12;
    this.quad(ctx, wx - 0.03, 0, wBot, wx + wW + 0.03, 0, wBot,
                   wx + wW + 0.03, sillDepth, wBot, wx - 0.03, sillDepth, wBot, "#d4b888");
    // Sill front face
    this.quad(ctx, wx - 0.03, sillDepth, wBot, wx + wW + 0.03, sillDepth, wBot,
                   wx + wW + 0.03, sillDepth, wBot - 0.03, wx - 0.03, sillDepth, wBot - 0.03, "#b8a070");

    // Moonbeam shaft
    if (quality >= 0.5) {
      const beamFloor = this.w2s(wx + 0.3, 2.8, 0);
      ctx.save();
      ctx.globalAlpha = 0.035 + 0.012 * Math.sin(t * 0.4);
      ctx.beginPath();
      ctx.moveTo(tl.x, tl.y); ctx.lineTo(tr.x, tr.y);
      ctx.lineTo(beamFloor.x + 45 * this.sc, beamFloor.y);
      ctx.lineTo(beamFloor.x - 25 * this.sc, beamFloor.y);
      ctx.closePath();
      ctx.fillStyle = "#8090c0";
      ctx.fill();
      ctx.restore();
    }
  }

  // ── Fire reflection on window glass ─────────────────────────────────────────

  private _drawFireReflectionOnWindow(ctx: CanvasRenderingContext2D, t: number): void {
    const wx = 2.8, wBot = 0.5, wTop = 1.8, wW = 0.9;
    const bl = this.w2s(wx, 0, wBot), br = this.w2s(wx + wW, 0, wBot);
    const tr = this.w2s(wx + wW, 0, wTop), tl = this.w2s(wx, 0, wTop);

    const fk = 0.5 + 0.5 * Math.sin(t * 3.3);
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(tl.x, tl.y); ctx.lineTo(tr.x, tr.y);
    ctx.lineTo(br.x, br.y); ctx.lineTo(bl.x, bl.y);
    ctx.clip();

    // Warm glow on the lower-left panes (closer to fire)
    const cx = (bl.x + tl.x) / 2;
    const cy = (bl.y + tl.y) / 2;
    const gr = 25 * this.sc;
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, gr);
    g.addColorStop(0, `rgba(255, 120, 40, ${0.10 * fk})`);
    g.addColorStop(0.6, `rgba(200, 70, 20, ${0.04 * fk})`);
    g.addColorStop(1, "rgba(150, 40, 0, 0)");
    ctx.fillStyle = g;
    ctx.fillRect(cx - gr, cy - gr, gr * 2, gr * 2);
    ctx.restore();
  }

  // ── Furniture shadows on floor ─────────────────────────────────────────────

  private _drawFurnitureShadows(ctx: CanvasRenderingContext2D, t: number): void {
    const fk = 0.7 + 0.3 * Math.sin(t * 3.3);
    const shadowAlpha = 0.06 + 0.03 * (1 - fk);

    // Shadow under armchair — offset away from fire
    const chairBase = this.w2s(1.7, 2.2, 0);
    ctx.globalAlpha = shadowAlpha;
    ctx.fillStyle = "#000";
    ctx.beginPath();
    ctx.ellipse(chairBase.x + 4 * this.sc, chairBase.y, 14 * this.sc, 8 * this.sc, -0.4, 0, Math.PI * 2);
    ctx.fill();

    // Shadow under table — smaller, more defined
    const tableBase = this.w2s(3.8, 0.8, 0);
    ctx.beginPath();
    ctx.ellipse(tableBase.x, tableBase.y, 12 * this.sc, 6 * this.sc, -0.4, 0, Math.PI * 2);
    ctx.fill();

    // Shadow along bookcase base
    const bookBase = this.w2s(0.15, 1.5, 0);
    ctx.beginPath();
    ctx.ellipse(bookBase.x, bookBase.y, 5 * this.sc, 16 * this.sc, -0.8, 0, Math.PI * 2);
    ctx.fill();

    ctx.globalAlpha = 1;
  }

  // ── Picture frame on west wall ─────────────────────────────────────────────

  private _drawPictureFrame(ctx: CanvasRenderingContext2D): void {
    const fy = 1.5, fz = 0.7, fH = 0.45, fD = 0.55;
    // Gilt frame
    this.quad(ctx, 0, fy, fz + fH, 0, fy + fD, fz + fH, 0, fy + fD, fz, 0, fy, fz, "#9a8060");
    // Inner bevel
    const p = 0.04;
    this.quad(ctx, 0, fy + p * 0.5, fz + fH - p * 0.5, 0, fy + fD - p * 0.5, fz + fH - p * 0.5,
                   0, fy + fD - p * 0.5, fz + p * 0.5, 0, fy + p * 0.5, fz + p * 0.5, "#b09870");
    // Canvas — night sky with hills
    const ip = 0.07;
    this.quad(ctx, 0, fy + ip, fz + fH - ip, 0, fy + fD - ip, fz + fH - ip,
                   0, fy + fD - ip, fz + ip, 0, fy + ip, fz + ip, "#0e1824");
    // Tiny stars in painting
    const ptl = this.w2s(0, fy + ip, fz + fH - ip);
    const pbr = this.w2s(0, fy + fD - ip, fz + ip);
    const pw = Math.abs(pbr.x - ptl.x);
    const ph = Math.abs(pbr.y - ptl.y);
    if (pw > 5) {
      ctx.fillStyle = "#c8d0e0";
      for (let s = 0; s < 5; s++) {
        const sx = ptl.x + (s * 0.19 + 0.12) * pw;
        const sy = ptl.y + (s * 0.13 + 0.15) * ph;
        ctx.globalAlpha = 0.4 + (s % 3) * 0.15;
        ctx.beginPath(); ctx.arc(sx, sy, 0.4, 0, Math.PI * 2); ctx.fill();
      }
      ctx.globalAlpha = 1;
    }
    // Rolling hills silhouette
    this.quad(ctx, 0, fy + ip, fz + ip + (fH - 2 * ip) * 0.38,
                   0, fy + fD - ip, fz + ip + (fH - 2 * ip) * 0.32,
                   0, fy + fD - ip, fz + ip, 0, fy + ip, fz + ip, "#1a2e22");
    // Second hill layer — slightly lighter
    this.quad(ctx, 0, fy + ip + 0.12, fz + ip + (fH - 2 * ip) * 0.28,
                   0, fy + fD - ip, fz + ip + (fH - 2 * ip) * 0.22,
                   0, fy + fD - ip, fz + ip, 0, fy + ip + 0.12, fz + ip, "#2a3e2e");
    // Tiny moon
    if (pw > 5) {
      const moonPt = this.w2s(0, fy + fD * 0.3, fz + fH * 0.78);
      ctx.fillStyle = "#e0dcc8";
      ctx.globalAlpha = 0.7;
      ctx.beginPath(); ctx.arc(moonPt.x, moonPt.y, 1.2, 0, Math.PI * 2); ctx.fill();
      ctx.globalAlpha = 1;
    }
    // Frame shadow on wall
    ctx.globalAlpha = 0.04;
    ctx.fillStyle = "#000";
    const shd = this.w2s(0, fy + 0.05, fz - 0.03);
    ctx.beginPath();
    ctx.ellipse(shd.x, shd.y + 2, 3, 12 * this.sc, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  // ── Fireplace — the heart ─────────────────────────────────────────────────

  private _drawFireplace(ctx: CanvasRenderingContext2D, t: number, quality: number): void {
    const fx = 0.2, fw = 1.2, fh = 1.1;

    // Stone surround
    this.quad(ctx, fx, 0, fh, fx + fw, 0, fh, fx + fw, 0, 0, fx, 0, 0, P.stone);

    // Stone texture — staggered masonry mortar joints
    ctx.strokeStyle = "rgba(0,0,0,0.06)";
    ctx.lineWidth = 0.5;
    const rows = 6;
    for (let i = 1; i < rows; i++) {
      const z = fh * (i / rows);
      const sl = this.w2s(fx, 0, z), sr = this.w2s(fx + fw, 0, z);
      ctx.beginPath(); ctx.moveTo(sl.x, sl.y); ctx.lineTo(sr.x, sr.y); ctx.stroke();
      // Staggered vertical joints — offset by half a stone width on alternating rows
      const stones = 3;
      const offset = (i % 2) * 0.5;
      for (let j = 0; j < stones; j++) {
        const vx = fx + fw * ((j + offset + 0.5) / (stones + 0.5));
        if (vx > fx + fw) continue;
        const zBot = fh * (i / rows);
        const zTop = fh * ((i + 1) / rows);
        const vb = this.w2s(vx, 0, zBot), vt = this.w2s(vx, 0, Math.min(zTop, fh));
        ctx.beginPath(); ctx.moveTo(vb.x, vb.y); ctx.lineTo(vt.x, vt.y); ctx.stroke();
      }
    }
    // Single divider for the wider panels
    const vm1 = this.w2s(fx + fw * 0.5, 0, 0), vm2 = this.w2s(fx + fw * 0.5, 0, fh * (1 / rows));
    ctx.beginPath(); ctx.moveTo(vm1.x, vm1.y); ctx.lineTo(vm2.x, vm2.y); ctx.stroke();

    // Mantle
    const depth = 0.2;
    this.quad(ctx, fx - 0.06, 0, fh, fx + fw + 0.06, 0, fh, fx + fw + 0.06, depth, fh, fx - 0.06, depth, fh, P.stoneLight);
    this.quad(ctx, fx - 0.06, depth, fh, fx + fw + 0.06, depth, fh, fx + fw + 0.06, depth, fh - 0.08, fx - 0.06, depth, fh - 0.08, P.stoneDark);

    // Mantle clock — screen-space rendering anchored to isometric position.
    // We project the center onto the north wall, then draw in screen-space
    // with a tilt that matches the wall's isometric angle.
    const es = this.sc;
    const clkWx = fx + fw * 0.22;
    const faceCenter = this.w2s(clkWx, depth * 0.15, fh + 0.11);
    // Compute the wall's screen-space tilt from two x-axis points
    const wallRefL = this.w2s(clkWx - 0.1, 0, fh + 0.11);
    const wallRefR = this.w2s(clkWx + 0.1, 0, fh + 0.11);
    const faceAngle = Math.atan2(wallRefR.y - wallRefL.y, wallRefR.x - wallRefL.x);
    const clkR = 4.2 * es;  // screen-space face radius

    ctx.save();
    ctx.translate(faceCenter.x, faceCenter.y);
    ctx.rotate(faceAngle);

    // Clock body — dark wood housing with arched top
    const bodyW = clkR * 1.35, bodyH = clkR * 2.0;
    ctx.fillStyle = "#4a3828";
    ctx.beginPath();
    ctx.moveTo(-bodyW, bodyH * 0.40);
    ctx.lineTo(-bodyW, -bodyH * 0.30);
    ctx.quadraticCurveTo(-bodyW * 0.3, -bodyH * 0.55, 0, -bodyH * 0.60);
    ctx.quadraticCurveTo(bodyW * 0.3, -bodyH * 0.55, bodyW, -bodyH * 0.30);
    ctx.lineTo(bodyW, bodyH * 0.40);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = "#6a5438";
    ctx.lineWidth = 0.6;
    ctx.stroke();

    // Small feet at base
    ctx.fillStyle = "#5a4430";
    ctx.fillRect(-bodyW * 0.8, bodyH * 0.35, bodyW * 0.3, bodyH * 0.08);
    ctx.fillRect(bodyW * 0.5, bodyH * 0.35, bodyW * 0.3, bodyH * 0.08);

    // Clock face — ivory circle
    ctx.fillStyle = "#eee4d0";
    ctx.beginPath();
    ctx.arc(0, -bodyH * 0.05, clkR, 0, Math.PI * 2);
    ctx.fill();
    // Face rim — brass ring
    ctx.strokeStyle = "#c8a060";
    ctx.lineWidth = 0.7;
    ctx.stroke();
    // Inner ring
    ctx.strokeStyle = "rgba(180, 160, 130, 0.4)";
    ctx.lineWidth = 0.3;
    ctx.beginPath();
    ctx.arc(0, -bodyH * 0.05, clkR * 0.90, 0, Math.PI * 2);
    ctx.stroke();

    const faceCy = -bodyH * 0.05; // face center y in rotated space

    // Tick marks — 12 hour positions
    for (let h = 0; h < 12; h++) {
      const a = (h / 12) * Math.PI * 2 - Math.PI / 2;
      const inner = h % 3 === 0 ? 0.65 : 0.78;
      ctx.strokeStyle = h % 3 === 0 ? "#3a2a1a" : "#8a7a6a";
      ctx.lineWidth = h % 3 === 0 ? 0.8 : 0.4;
      ctx.beginPath();
      ctx.moveTo(Math.cos(a) * clkR * inner, faceCy + Math.sin(a) * clkR * inner);
      ctx.lineTo(Math.cos(a) * clkR * 0.88, faceCy + Math.sin(a) * clkR * 0.88);
      ctx.stroke();
    }

    // Hour hand — short, thick
    const hourAngle = (t * 0.05) % (Math.PI * 2);
    ctx.strokeStyle = "#1a0a00";
    ctx.lineWidth = 1.1;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(0, faceCy);
    ctx.lineTo(Math.sin(hourAngle) * clkR * 0.46, faceCy - Math.cos(hourAngle) * clkR * 0.46);
    ctx.stroke();

    // Minute hand — long, thin, 12× the hour
    const minAngle = hourAngle * 12;
    ctx.lineWidth = 0.55;
    ctx.beginPath();
    ctx.moveTo(0, faceCy);
    ctx.lineTo(Math.sin(minAngle) * clkR * 0.74, faceCy - Math.cos(minAngle) * clkR * 0.74);
    ctx.stroke();

    // Center pin — brass
    ctx.fillStyle = "#c8a050";
    ctx.beginPath();
    ctx.arc(0, faceCy, 0.5 * es, 0, Math.PI * 2);
    ctx.fill();
    ctx.lineCap = "butt";

    // Pendulum — swings below body
    const pendLen = bodyH * 0.55;
    const pendSwing = Math.sin(t * 2.8) * 0.28;
    const pendTipX = Math.sin(pendSwing) * pendLen;
    const pendTipY = bodyH * 0.40 + Math.cos(pendSwing) * pendLen;
    ctx.strokeStyle = "#6a5438";
    ctx.lineWidth = 0.4;
    ctx.beginPath();
    ctx.moveTo(0, bodyH * 0.40);
    ctx.lineTo(pendTipX, pendTipY);
    ctx.stroke();
    // Bob — golden disc
    ctx.fillStyle = "#c8a050";
    ctx.beginPath();
    ctx.arc(pendTipX, pendTipY, 1.4 * es, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = "#e8c870";
    ctx.beginPath();
    ctx.arc(pendTipX - 0.3 * es, pendTipY - 0.3 * es, 0.6 * es, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();

    // Clock shadow on mantle surface
    ctx.globalAlpha = 0.07;
    ctx.fillStyle = "#000";
    const clkShadow = this.w2s(clkWx + 0.04, depth * 0.5, fh);
    ctx.beginPath();
    ctx.ellipse(clkShadow.x, clkShadow.y, 3.5 * es, 1.5 * es, faceAngle, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;

    // Candle on mantle (right side)
    const candleX = fx + fw * 0.75;
    const candleBase = this.w2s(candleX, depth * 0.5, fh);
    const candleTop = this.w2s(candleX, depth * 0.5, fh + 0.15);
    // Candle shadow on mantle
    ctx.globalAlpha = 0.07;
    ctx.fillStyle = "#000";
    const candleShadow = this.w2s(candleX + 0.03, depth * 0.6, fh);
    ctx.beginPath();
    ctx.ellipse(candleShadow.x, candleShadow.y, 2.5 * es, 1.2 * es, faceAngle, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
    // Candle body — cream wax with slight taper
    ctx.strokeStyle = "#e8d8b0";
    ctx.lineWidth = 2.2;
    ctx.beginPath(); ctx.moveTo(candleBase.x, candleBase.y); ctx.lineTo(candleTop.x, candleTop.y); ctx.stroke();
    // Wax drip detail
    ctx.strokeStyle = "#f0e8d0";
    ctx.lineWidth = 0.6;
    ctx.beginPath();
    ctx.moveTo(candleTop.x - 0.8 * es, candleTop.y + 0.5 * es);
    ctx.quadraticCurveTo(candleTop.x - 1.2 * es, candleTop.y + 2.5 * es, candleTop.x - 0.6 * es, candleTop.y + 3.5 * es);
    ctx.stroke();
    // Wick
    ctx.strokeStyle = "#3a3028";
    ctx.lineWidth = 0.4;
    ctx.beginPath();
    ctx.moveTo(candleTop.x, candleTop.y);
    ctx.lineTo(candleTop.x, candleTop.y - 1.5 * es);
    ctx.stroke();
    // Flame — gentle dance, NOT frantic (was t*11, now t*3.5 with multi-harmonic)
    const cfBase = 0.7 + 0.2 * Math.sin(t * 3.5 + 2.5) + 0.1 * Math.sin(t * 7.1 + 1.3);
    const candleSway = Math.sin(t * 2.1) * 0.5 * es;
    // Outer flame glow
    ctx.globalAlpha = cfBase * 0.4;
    ctx.fillStyle = "#ff9030";
    ctx.beginPath();
    ctx.ellipse(candleTop.x + candleSway * 0.5, candleTop.y - 3.5 * es, Math.max(0.5, 3 * es), Math.max(0.5, 5 * es), 0, 0, Math.PI * 2);
    ctx.fill();
    // Main flame — teardrop shape
    ctx.globalAlpha = cfBase;
    ctx.fillStyle = "#ffe080";
    const cFlameH = 3.5 * es * cfBase;
    const cFlameW = 1.2 * es;
    ctx.beginPath();
    ctx.moveTo(candleTop.x, candleTop.y - 0.8 * es);
    ctx.bezierCurveTo(
      candleTop.x - cFlameW, candleTop.y - 1.5 * es,
      candleTop.x - cFlameW * 0.5, candleTop.y - cFlameH,
      candleTop.x + candleSway, candleTop.y - cFlameH - 1 * es
    );
    ctx.bezierCurveTo(
      candleTop.x + cFlameW * 0.5, candleTop.y - cFlameH,
      candleTop.x + cFlameW, candleTop.y - 1.5 * es,
      candleTop.x, candleTop.y - 0.8 * es
    );
    ctx.fill();
    // Inner core
    ctx.globalAlpha = cfBase * 0.7;
    ctx.fillStyle = "#fff8e0";
    ctx.beginPath();
    ctx.ellipse(candleTop.x + candleSway * 0.3, candleTop.y - 2.2 * es, Math.max(0.3, 0.5 * es), Math.max(0.3, 1.2 * es * cfBase), 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;

    // Firebox opening — with arch lintel
    const aX = fx + 0.22, aW = fw - 0.44, aH = 0.62;
    const aA = this.w2s(aX, 0, aH), aB = this.w2s(aX + aW, 0, aH);
    const aC = this.w2s(aX + aW, 0, 0.02), aD = this.w2s(aX, 0, 0.02);

    // Dark firebox interior
    ctx.beginPath();
    ctx.moveTo(aA.x, aA.y); ctx.lineTo(aB.x, aB.y);
    ctx.lineTo(aC.x, aC.y); ctx.lineTo(aD.x, aD.y);
    ctx.closePath();
    ctx.fillStyle = P.hearth;
    ctx.fill();

    // Arch lintel above firebox — subtle curved stone header
    const archMid = this.w2s(aX + aW / 2, 0, aH + 0.06);
    ctx.strokeStyle = P.stoneLight;
    ctx.lineWidth = 1.8;
    ctx.beginPath();
    ctx.moveTo(aA.x, aA.y);
    ctx.quadraticCurveTo(archMid.x, archMid.y, aB.x, aB.y);
    ctx.stroke();
    // Keystone
    const ks = this.w2s(aX + aW / 2, 0, aH + 0.04);
    ctx.fillStyle = P.stoneLight;
    ctx.beginPath();
    ctx.moveTo(ks.x - 2.5 * es, ks.y + 1 * es);
    ctx.lineTo(ks.x - 1.5 * es, ks.y - 2 * es);
    ctx.lineTo(ks.x + 1.5 * es, ks.y - 2 * es);
    ctx.lineTo(ks.x + 2.5 * es, ks.y + 1 * es);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.1)";
    ctx.lineWidth = 0.4;
    ctx.stroke();

    // Inner firebox depth shadow — makes the opening look recessed
    ctx.globalAlpha = 0.25;
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(aA.x + 1, aA.y + 1); ctx.lineTo(aD.x + 1, aD.y - 1);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(aB.x - 1, aB.y + 1); ctx.lineTo(aC.x - 1, aC.y - 1);
    ctx.stroke();
    ctx.globalAlpha = 1;

    // ── THE FIRE ────────────────────────────────────────────────────────
    if (quality >= 0.3) {
      const aw = Math.abs(aB.x - aA.x);
      const ah = Math.abs(aC.y - aA.y);
      // Isometric center of the firebox floor (midpoint of aD→aC diagonal)
      const midx = (aD.x + aC.x) / 2;
      const midy = (aD.y + aC.y) / 2;

      // Ember bed glow — centered on the isometric diagonal
      const ef = 0.75 + 0.25 * Math.sin(t * 4.7);
      const eg = ctx.createRadialGradient(midx, midy, 0, midx, midy, Math.max(1, aw * 0.65));
      eg.addColorStop(0,   `rgba(255, 140, 30, ${0.92 * ef})`);
      eg.addColorStop(0.4, `rgba(210, 70, 10, ${0.55 * ef})`);
      eg.addColorStop(1,   "rgba(80, 20, 0, 0)");
      ctx.fillStyle = eg;
      ctx.fillRect(midx - aw * 0.75, midy - ah * 0.4, aw * 1.5, ah * 0.8);

      // Log silhouettes — positioned along the isometric diagonal
      ctx.fillStyle = "rgba(25, 8, 0, 0.45)";
      for (let i = 0; i < 3; i++) {
        const frac = (i + 0.5) / 3;
        const lx = aD.x + (aC.x - aD.x) * frac;
        const ly = aD.y + (aC.y - aD.y) * frac;
        // Rotate logs to follow the isometric angle of the firebox
        const isoAngle = Math.atan2(aC.y - aD.y, aC.x - aD.x);
        const logAngle = isoAngle + (i - 1) * 0.25;
        ctx.save();
        ctx.translate(lx, ly);
        ctx.rotate(logAngle);
        ctx.fillRect(-aw * 0.09, -ah * 0.025, aw * 0.18, ah * 0.05);
        ctx.restore();
      }

      // 7 flame tongues — bezier teardrops with pointed tips and sway
      // Bases follow the isometric diagonal of the firebox opening (aD→aC)
      const flames = [
        { frac: 0.08, h: 0.38, f: 9.1, p: 3.0, w: 0.08, sw: 0.015 },
        { frac: 0.20, h: 0.62, f: 6.3, p: 0.5, w: 0.12, sw: 0.025 },
        { frac: 0.33, h: 0.82, f: 5.1, p: 1.2, w: 0.15, sw: 0.030 },
        { frac: 0.47, h: 1.00, f: 4.5, p: 2.8, w: 0.17, sw: 0.025 },
        { frac: 0.60, h: 0.78, f: 5.7, p: 0.0, w: 0.14, sw: 0.030 },
        { frac: 0.74, h: 0.52, f: 7.2, p: 1.8, w: 0.10, sw: 0.020 },
        { frac: 0.88, h: 0.33, f: 8.8, p: 2.5, w: 0.07, sw: 0.012 },
      ];

      for (const fl of flames) {
        const flicker = 0.65 + 0.35 * Math.sin(t * fl.f + fl.p);
        // Interpolate along the firebox bottom edge (isometric diagonal)
        const ffx = aD.x + (aC.x - aD.x) * fl.frac;
        const ffy = aD.y + (aC.y - aD.y) * fl.frac;
        const ffh = ah * fl.h * flicker;
        const ffw = Math.max(1, aw * fl.w);
        const sway = Math.sin(t * fl.f * 0.7 + fl.p + 1) * aw * fl.sw;

        const fg = ctx.createLinearGradient(ffx, ffy, ffx, ffy - ffh);
        fg.addColorStop(0,   `rgba(255, 200, 60, ${0.88 * flicker})`);
        fg.addColorStop(0.25, `rgba(255, 140, 20, ${0.75 * flicker})`);
        fg.addColorStop(0.6, `rgba(255, 70, 0, ${0.40 * flicker})`);
        fg.addColorStop(1,   "rgba(255, 40, 0, 0)");

        ctx.beginPath();
        ctx.moveTo(ffx, ffy);
        ctx.bezierCurveTo(
          ffx - ffw * 0.6, ffy - ffh * 0.3,
          ffx - ffw * 0.35, ffy - ffh * 0.75,
          ffx + sway, ffy - ffh
        );
        ctx.bezierCurveTo(
          ffx + ffw * 0.35, ffy - ffh * 0.75,
          ffx + ffw * 0.6, ffy - ffh * 0.3,
          ffx, ffy
        );
        ctx.closePath();
        ctx.fillStyle = fg;
        ctx.fill();
      }

      // White-hot core — positioned along the isometric diagonal
      const coreFlicker = 0.5 + 0.5 * Math.sin(t * 13);
      ctx.globalAlpha = coreFlicker * 0.7;
      ctx.fillStyle = "#fff8e0";
      const core1x = aD.x + (aC.x - aD.x) * 0.38;
      const core1y = aD.y + (aC.y - aD.y) * 0.38 - ah * 0.06;
      ctx.beginPath();
      ctx.arc(core1x, core1y, Math.max(0.5, aw * 0.04), 0, Math.PI * 2);
      ctx.fill();
      const core2x = aD.x + (aC.x - aD.x) * 0.58;
      const core2y = aD.y + (aC.y - aD.y) * 0.58 - ah * 0.03;
      ctx.beginPath();
      ctx.arc(core2x, core2y, Math.max(0.5, aw * 0.03), 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    // Spawn embers — some drift high enough to become stars
    if (quality >= 0.4 && Math.random() < 0.35) {
      const emberSpawn = this.w2s(fx + fw / 2, 0.05, 0.5);
      this.embers.push({
        x: emberSpawn.x + (Math.random() - 0.5) * 12, y: emberSpawn.y,
        vx: (Math.random() - 0.5) * 12, vy: -(30 + Math.random() * 50),
        life: 2.5 + Math.random() * 3.5, max: 0, s: 0.7 + Math.random() * 1.5,
        hue: 15 + Math.random() * 35,
      });
      this.embers[this.embers.length - 1].max = this.embers[this.embers.length - 1].life;
    }

    // Floor glow + wall glow
    const floorPt = this.w2s(fx + fw / 2, 1.3, 0);
    const gr = 110 * this.sc;
    const fk = 0.72 + 0.28 * Math.sin(t * 3.5);
    const flg = ctx.createRadialGradient(floorPt.x, floorPt.y, 0, floorPt.x, floorPt.y, gr);
    flg.addColorStop(0,   `rgba(255, 120, 30, ${0.24 * fk})`);
    flg.addColorStop(0.35, `rgba(200, 70, 10, ${0.10 * fk})`);
    flg.addColorStop(1,   "rgba(150, 40, 0, 0)");
    ctx.fillStyle = flg;
    ctx.fillRect(floorPt.x - gr, floorPt.y - gr, gr * 2, gr * 2);

    const wallPt = this.w2s(fx + fw / 2, 0, 0.5);
    const wg = ctx.createRadialGradient(wallPt.x, wallPt.y, 0, wallPt.x, wallPt.y, 65 * this.sc);
    wg.addColorStop(0, `rgba(255, 130, 45, ${0.14 * fk})`);
    wg.addColorStop(1, "rgba(200, 70, 20, 0)");
    ctx.fillStyle = wg;
    ctx.fillRect(wallPt.x - 75 * this.sc, wallPt.y - 75 * this.sc, 150 * this.sc, 150 * this.sc);
  }

  // ── Bookcase ───────────────────────────────────────────────────────────────

  private _drawBookcase(ctx: CanvasRenderingContext2D): void {
    const bx = 0, by = 0.7, bW = 0.22, bH = 1.5, bD = 1.3;

    this.quad(ctx, bx, by, bH, bx, by + bD, bH, bx, by + bD, 0, bx, by, 0, P.woodDark);
    this.quad(ctx, bx + bW, by, bH, bx + bW, by + bD, bH, bx + bW, by + bD, 0, bx + bW, by, 0, "#7a5a30");

    const bookColors = ["#b83a3a", "#3a5ab4", "#3a9a3a", "#c49a3a", "#8a3ab4", "#3ab4a4", "#c46a3a", "#6a3a8a", "#8a5a3a", "#4a7a5a"];
    const shelfCount = 4;
    for (let s = 0; s < shelfCount; s++) {
      const z0 = bH * ((s + 0.12) / shelfCount);
      const z1 = bH * ((s + 0.88) / shelfCount);

      const sl = this.w2s(bx + bW, by, z0), sr = this.w2s(bx + bW, by + bD, z0);
      ctx.strokeStyle = "rgba(60,40,20,0.5)";
      ctx.lineWidth = 1.2;
      ctx.beginPath(); ctx.moveTo(sl.x, sl.y); ctx.lineTo(sr.x, sr.y); ctx.stroke();

      const count = 4 + s;
      for (let i = 0; i < count; i++) {
        const f0 = (i + 0.05) / count;
        const f1 = (i + 0.72) / count;
        const bookH = (z1 - z0) * (0.55 + ((i * 37 + s * 13) % 17) / 42);
        this.quad(ctx,
          bx + bW, by + bD * f0, z0 + 0.02,
          bx + bW, by + bD * f1, z0 + 0.02,
          bx + bW, by + bD * f1, z0 + bookH,
          bx + bW, by + bD * f0, z0 + bookH,
          bookColors[(i + s * 3) % bookColors.length]);
      }
    }

    // Fire warm highlight on book spines — books nearer the fire catch orange light
    const bookGlow = this.w2s(bx + bW, by + bD * 0.15, bH * 0.4);
    ctx.globalAlpha = 0.10;
    const bgr = 20 * this.sc;
    const bgg = ctx.createRadialGradient(bookGlow.x, bookGlow.y, 0, bookGlow.x, bookGlow.y, bgr);
    bgg.addColorStop(0, "rgba(255, 140, 50, 0.9)");
    bgg.addColorStop(1, "rgba(255, 100, 20, 0)");
    ctx.fillStyle = bgg;
    ctx.fillRect(bookGlow.x - bgr, bookGlow.y - bgr, bgr * 2, bgr * 2);
    ctx.globalAlpha = 1;

    // Top + south face
    this.quad(ctx, bx, by, bH, bx + bW, by, bH, bx + bW, by + bD, bH, bx, by + bD, bH, "#9a7a4a");
    this.quad(ctx, bx, by + bD, bH, bx + bW, by + bD, bH, bx + bW, by + bD, 0, bx, by + bD, 0, P.woodMid);

    // One book leaning on top — charming detail
    const leanBase = this.w2s(bx + bW * 0.4, by + bD * 0.3, bH);
    const leanTop = this.w2s(bx + bW * 0.6, by + bD * 0.5, bH + 0.08);
    ctx.strokeStyle = "#c46a3a";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(leanBase.x, leanBase.y);
    ctx.lineTo(leanTop.x, leanTop.y);
    ctx.stroke();
    ctx.lineCap = "butt";
  }

  // ── Armchair ───────────────────────────────────────────────────────────────

  private _drawArmchair(ctx: CanvasRenderingContext2D, t: number): void {
    const cx = 1.3, cy = 1.6, cW = 0.62, cD = 0.58;
    const seatZ = 0.26, backZ = 0.75, armZ = 0.44, armW = 0.11;

    // Body
    this.quad(ctx, cx, cy + cD, 0, cx + cW, cy + cD, 0, cx + cW, cy + cD, seatZ, cx, cy + cD, seatZ, P.chairDark);
    this.quad(ctx, cx + cW, cy, 0, cx + cW, cy + cD, 0, cx + cW, cy + cD, seatZ, cx + cW, cy, seatZ, P.chair);

    // Seat
    this.quad(ctx, cx, cy, seatZ, cx + cW, cy, seatZ, cx + cW, cy + cD, seatZ, cx, cy + cD, seatZ, P.chairSeat);

    // Cushion
    const p = 0.07;
    this.quad(ctx, cx + p, cy + p, seatZ + 0.03, cx + cW - p, cy + p, seatZ + 0.03,
                   cx + cW - p, cy + cD - 0.13, seatZ + 0.03, cx + p, cy + cD - 0.13, seatZ + 0.03, P.cushion);
    const fk = 0.3 + 0.15 * Math.sin(t * 3.5);
    ctx.globalAlpha = fk;
    this.quad(ctx, cx + p, cy + p, seatZ + 0.035, cx + cW * 0.5, cy + p, seatZ + 0.035,
                   cx + cW * 0.5, cy + cD * 0.4, seatZ + 0.035, cx + p, cy + cD * 0.4, seatZ + 0.035, P.cushionHi);
    ctx.globalAlpha = 1;

    // Back rest
    this.quad(ctx, cx, cy + cD - 0.12, seatZ, cx + cW, cy + cD - 0.12, seatZ, cx + cW, cy + cD - 0.12, backZ, cx, cy + cD - 0.12, backZ, P.chair);
    this.quad(ctx, cx, cy + cD - 0.12, backZ, cx + cW, cy + cD - 0.12, backZ, cx + cW, cy + cD, backZ, cx, cy + cD, backZ, "#9a7a5a");
    this.quad(ctx, cx + cW, cy + cD - 0.12, seatZ, cx + cW, cy + cD, seatZ, cx + cW, cy + cD, backZ, cx + cW, cy + cD - 0.12, backZ, P.chairDark);

    // Right arm
    this.quad(ctx, cx + cW - armW, cy, seatZ, cx + cW, cy, seatZ, cx + cW, cy + cD, armZ, cx + cW - armW, cy + cD, armZ, P.chair);
    this.quad(ctx, cx + cW - armW, cy, armZ, cx + cW, cy, armZ, cx + cW, cy + cD, armZ, cx + cW - armW, cy + cD, armZ, "#9a7a5a");

    // Left arm (south face)
    this.quad(ctx, cx, cy + cD, seatZ, cx + armW, cy + cD, seatZ, cx + armW, cy + cD, armZ, cx, cy + cD, armZ, P.chairDark);
  }

  // ── Cat — pose-based skeletal animation ─────────────────────────────────────
  //
  // Architecture: state machine sets a TARGET pose, every frame we lerp the
  // CURRENT pose toward the target. Drawing reads only from the current pose
  // and a skeleton: hip → spine → shoulder → head, hip → tail, shoulder → legs.
  // Nothing is positioned independently — everything chains from the hip.

  private _catTargetForState(state: CatState, t: number): Partial<CatPose> {
    switch (state) {
      case "sleep":   return { spineAngle: 0, headLift: 0, headTurn: 0, jawOpen: 0, eyeOpen: 0, tailWag: 0, scratchLeg: 0, tailCurl: 0.3 };
      case "alert":   return { spineAngle: 0.05, headLift: 0.6, headTurn: Math.sin(t * 0.3) * 0.3, eyeOpen: 1, tailWag: 0.15, tailCurl: 0.3 };
      case "sit":     return { spineAngle: 0.6, headLift: 0.8, headTurn: 0, eyeOpen: 1, tailCurl: 0.5, tailWag: 0.1, scratchLeg: 0 };
      case "walk":    return { spineAngle: 0.45, headLift: 0.7, eyeOpen: 1, tailWag: 0.25, tailCurl: 0.4, scratchLeg: 0 };
      case "scratch": return { spineAngle: 0.35, headLift: 0.4, headTurn: -0.4, eyeOpen: 0.6, scratchLeg: 1, tailWag: 0.05 };
      case "yawn":    return { spineAngle: 0.05, headLift: 0.5, jawOpen: 1, eyeOpen: 0.3, tailWag: 0 };
      case "groom":   return { spineAngle: 0.3, headLift: 0.2, headTurn: -0.6, eyeOpen: 0.5, jawOpen: 0.15, tailWag: 0, tailCurl: 0.6 };
      default:        return {};
    }
  }

  private _updateCatState(t: number, dt: number): void {
    const ca = this.catAnim;
    ca.stateT += dt;

    // ── State transitions ────────────────────────────────────────
    if (ca.stateT >= ca.nextChange) {
      const r = Math.random();
      const prev = ca.state;
      if (prev === "sleep") {
        if (r < 0.40)      { ca.state = "alert";   ca.nextChange = 3 + Math.random() * 5; }
        else if (r < 0.65) { ca.state = "yawn";    ca.nextChange = 2.5 + Math.random() * 1.5; }
        else if (r < 0.85) { ca.state = "groom";   ca.nextChange = 3 + Math.random() * 3; }
        else               { ca.state = "scratch"; ca.nextChange = 2 + Math.random() * 2; }
      } else if (prev === "alert") {
        if (r < 0.30)      { ca.state = "sleep";   ca.nextChange = 10 + Math.random() * 15; }
        else if (r < 0.55) { ca.state = "sit";     ca.nextChange = 4 + Math.random() * 6; }
        else if (r < 0.75) { ca.state = "scratch"; ca.nextChange = 2 + Math.random() * 2; }
        else               { ca.state = "groom";   ca.nextChange = 3 + Math.random() * 3; }
      } else if (prev === "sit") {
        if (r < 0.35)      { ca.state = "walk";    ca.nextChange = 3 + Math.random() * 3; ca.walkDir = r < 0.17 ? -1 : 1; }
        else if (r < 0.55) { ca.state = "scratch"; ca.nextChange = 2 + Math.random() * 2; }
        else if (r < 0.80) { ca.state = "sleep";   ca.nextChange = 8 + Math.random() * 15; }
        else               { ca.state = "alert";   ca.nextChange = 3 + Math.random() * 4; }
      } else if (prev === "walk") {
        if (r < 0.50)      { ca.state = "sit";     ca.nextChange = 4 + Math.random() * 5; }
        else               { ca.state = "sleep";   ca.nextChange = 10 + Math.random() * 15; }
      } else { // scratch, yawn, groom → mostly back to sleep/alert
        if (r < 0.50)      { ca.state = "sleep";   ca.nextChange = 8 + Math.random() * 15; }
        else               { ca.state = "alert";   ca.nextChange = 3 + Math.random() * 5; }
      }
      ca.stateT = 0;
      // Set new target pose
      Object.assign(ca.target, TitleRenderer.CAT_ZERO_POSE, this._catTargetForState(ca.state, t));
    }

    // ── Lerp current pose toward target ──────────────────────────
    const p = ca.pose, tgt = ca.target;
    const lerpRate = 3.5 * dt; // smooth ~0.3s blend
    p.spineAngle += (tgt.spineAngle - p.spineAngle) * lerpRate;
    p.headLift   += (tgt.headLift   - p.headLift)   * lerpRate;
    p.headTurn   += (tgt.headTurn   - p.headTurn)   * lerpRate;
    p.jawOpen    += (tgt.jawOpen    - p.jawOpen)     * lerpRate * 2;
    p.eyeOpen    += (tgt.eyeOpen    - p.eyeOpen)     * lerpRate * 2;
    p.tailCurl   += (tgt.tailCurl   - p.tailCurl)    * lerpRate;
    p.tailWag    += (tgt.tailWag    - p.tailWag)     * lerpRate;
    p.scratchLeg += (tgt.scratchLeg - p.scratchLeg)  * lerpRate * 4;

    // Ear twitches — procedural noise layered on top
    p.earLTwitch = Math.sin(t * 7.3 + 1.2) * 0.15 * p.headLift + (ca.state === "alert" ? Math.sin(t * 11) * 0.3 : 0);
    p.earRTwitch = Math.sin(t * 6.1 + 3.4) * 0.12 * p.headLift + (ca.state === "alert" ? Math.sin(t * 9 + 2) * 0.25 : 0);

    // Walk offset
    if (ca.state === "walk") {
      p.walkX += dt * 0.08 * ca.walkDir;
      p.walkX = Math.max(-0.18, Math.min(0.18, p.walkX));
      // Walk leg cycling
      p.frontLegLPhase = (p.frontLegLPhase + dt * 5) % (Math.PI * 2);
      p.frontLegRPhase = p.frontLegLPhase + Math.PI;
      p.backLegLPhase  = p.frontLegLPhase + Math.PI * 0.5;
      p.backLegRPhase  = p.frontLegLPhase + Math.PI * 1.5;
    } else {
      p.walkX *= (1 - dt * 2);
      if (Math.abs(p.walkX) < 0.003) p.walkX = 0;
    }
  }

  private _drawCat(ctx: CanvasRenderingContext2D, t: number): void {
    this._updateCatState(t, 1 / 60);
    const ca = this.catAnim;
    const p = ca.pose;
    const s = this.sc;

    // ── Skeleton: compute joint positions from pose ──────────────
    // All coordinates are screen-space, chained from hip.

    // Hip — root joint, world position
    const hipWorld = this.w2s(2.3 + p.walkX, 1.8, 0.02);
    const hipX = hipWorld.x;
    const hipY = hipWorld.y;

    // Breathing — subtle vertical bob
    const breathe = Math.sin(t * (p.eyeOpen > 0.5 ? 1.4 : 0.85)) * (p.eyeOpen > 0.5 ? 1.0 : 1.8) * s;

    // Spine rises with spineAngle: 0=flat, 1=upright
    // Shoulder is forward-up from hip along the spine
    const spineLen = 10 * s;
    const spineAng = -0.45 - p.spineAngle * 0.9; // screen-space angle (more negative = more upright)
    const shoulderX = hipX + Math.cos(spineAng) * spineLen;
    const shoulderY = hipY + Math.sin(spineAng) * spineLen + breathe;

    // Neck: extends from shoulder upward
    const neckLen = 5 * s;
    const neckAng = spineAng - 0.3 - p.headLift * 0.6;
    const neckTopX = shoulderX + Math.cos(neckAng) * neckLen;
    const neckTopY = shoulderY + Math.sin(neckAng) * neckLen;

    // Head center — offset from neck top + turn
    const headX = neckTopX + p.headTurn * 2 * s;
    const headY = neckTopY;
    const headR = 3.2 * s;

    // ── Draw order: back legs → tail → body → front legs → head ──

    // ── Back legs (from hip) ─────────────────────────────────────
    const floorY = hipWorld.y + 4 * s; // approximate floor level
    if (p.spineAngle > 0.15) {
      const blegVis = Math.min(1, (p.spineAngle - 0.15) / 0.3);
      ctx.globalAlpha = blegVis;
      ctx.fillStyle = "#252530";
      // Back-left
      const blStep = ca.state === "walk" ? Math.sin(p.backLegLPhase) * 2 * s : 0;
      ctx.beginPath();
      ctx.moveTo(hipX + 1 * s, hipY);
      ctx.lineTo(hipX + 0 * s + blStep, floorY);
      ctx.lineTo(hipX + 2 * s + blStep, floorY);
      ctx.lineTo(hipX + 3 * s, hipY);
      ctx.fill();
      // Back-right
      const brStep = ca.state === "walk" ? Math.sin(p.backLegRPhase) * 2 * s : 0;
      ctx.beginPath();
      ctx.moveTo(hipX + 3.5 * s, hipY - 0.5 * s);
      ctx.lineTo(hipX + 3 * s + brStep, floorY);
      ctx.lineTo(hipX + 5 * s + brStep, floorY);
      ctx.lineTo(hipX + 5 * s, hipY - 0.5 * s);
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    // ── Tail — bezier from hip rearward ──────────────────────────
    const tailBaseX = hipX + 5 * s;
    const tailBaseY = hipY + 1 * s;
    const wag = Math.sin(t * 3.5) * p.tailWag * 8 * s;
    const curl = p.tailCurl;
    ctx.strokeStyle = P.cat;
    ctx.lineWidth = 2 * s;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(tailBaseX, tailBaseY);
    ctx.bezierCurveTo(
      tailBaseX + 6 * s, tailBaseY - 3 * s + wag * 0.3,
      tailBaseX + 10 * s, tailBaseY - (6 + curl * 4) * s + wag * 0.7,
      tailBaseX + 8 * s + wag, tailBaseY - (8 + curl * 5) * s + wag
    );
    ctx.stroke();
    ctx.lineCap = "butt";

    // ── Scratch leg — back leg reaching up to head ───────────────
    if (p.scratchLeg > 0.1) {
      const scratchCycle = Math.sin(t * 18) * p.scratchLeg;
      ctx.strokeStyle = P.cat;
      ctx.lineWidth = 1.5 * s;
      ctx.lineCap = "round";
      ctx.beginPath();
      ctx.moveTo(hipX + 3 * s, hipY);
      ctx.quadraticCurveTo(
        hipX - 2 * s, hipY - 6 * s + scratchCycle * 2 * s,
        headX + 2 * s, headY + headR * 0.6 + scratchCycle * 1.5 * s
      );
      ctx.stroke();
      // Paw at scratch point
      ctx.fillStyle = P.cat;
      ctx.beginPath();
      ctx.arc(headX + 2 * s, headY + headR * 0.6 + scratchCycle * 1.5 * s, 1 * s, 0, Math.PI * 2);
      ctx.fill();
      ctx.lineCap = "butt";
    }

    // ── Body — elongated ellipse along the spine ─────────────────
    const bodyCx = (hipX + shoulderX) / 2;
    const bodyCy = (hipY + shoulderY) / 2;
    const bodyLen = spineLen * 0.65;
    const bodyWid = (3.5 - p.spineAngle * 0.8) * s;
    ctx.fillStyle = P.cat;
    ctx.beginPath();
    ctx.ellipse(bodyCx, bodyCy, bodyLen, bodyWid, spineAng, 0, Math.PI * 2);
    ctx.fill();

    // ── Haunches — rounded rump at hip ───────────────────────────
    ctx.beginPath();
    ctx.ellipse(hipX + 1 * s, hipY - 0.5 * s, 4 * s, 3.5 * s, spineAng + 0.3, 0, Math.PI * 2);
    ctx.fill();

    // ── Chest — rounded front at shoulder ────────────────────────
    ctx.beginPath();
    ctx.ellipse(shoulderX, shoulderY, 3.5 * s, bodyWid * 0.9, spineAng - 0.2, 0, Math.PI * 2);
    ctx.fill();

    // ── Front legs (from shoulder down to floor) ─────────────────
    if (p.spineAngle > 0.12) {
      const flegVis = Math.min(1, (p.spineAngle - 0.12) / 0.25);
      ctx.globalAlpha = flegVis;
      ctx.fillStyle = P.cat;
      const flStep = ca.state === "walk" ? Math.sin(p.frontLegLPhase) * 2 * s : 0;
      const frStep = ca.state === "walk" ? Math.sin(p.frontLegRPhase) * 2 * s : 0;
      // Left front
      ctx.beginPath();
      ctx.moveTo(shoulderX - 1.5 * s, shoulderY + 1 * s);
      ctx.lineTo(shoulderX - 2 * s + flStep, floorY);
      ctx.lineTo(shoulderX - 0.5 * s + flStep, floorY);
      ctx.lineTo(shoulderX + 0 * s, shoulderY + 1 * s);
      ctx.fill();
      // Right front
      ctx.beginPath();
      ctx.moveTo(shoulderX + 0.5 * s, shoulderY + 0.5 * s);
      ctx.lineTo(shoulderX + 0 * s + frStep, floorY);
      ctx.lineTo(shoulderX + 1.5 * s + frStep, floorY);
      ctx.lineTo(shoulderX + 2 * s, shoulderY + 0.5 * s);
      ctx.fill();
      // Paws
      ctx.fillStyle = "#323238";
      for (const px of [shoulderX - 1.2 * s + flStep, shoulderX + 0.8 * s + frStep]) {
        ctx.beginPath();
        ctx.ellipse(px, floorY, 1.3 * s, 0.6 * s, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }

    // ── Head ─────────────────────────────────────────────────────
    ctx.fillStyle = P.cat;
    ctx.beginPath();
    ctx.arc(headX, headY, headR, 0, Math.PI * 2);
    ctx.fill();

    // Ears
    const earH = 3.5 * s;
    const earW = 2.2 * s;
    // Left ear
    const elx = headX - headR * 0.55, ely = headY - headR * 0.5;
    ctx.fillStyle = P.cat;
    ctx.beginPath();
    ctx.moveTo(elx - earW * 0.4, ely);
    ctx.lineTo(elx + p.earLTwitch * earW * 0.5, ely - earH);
    ctx.lineTo(elx + earW * 0.5, ely);
    ctx.fill();
    ctx.fillStyle = "rgba(180,100,100,0.3)";
    ctx.beginPath();
    ctx.moveTo(elx - earW * 0.2, ely - 0.5 * s);
    ctx.lineTo(elx + p.earLTwitch * earW * 0.5, ely - earH + 0.7 * s);
    ctx.lineTo(elx + earW * 0.3, ely - 0.5 * s);
    ctx.fill();
    // Right ear
    const erx = headX + headR * 0.45, ery = headY - headR * 0.6;
    ctx.fillStyle = P.cat;
    ctx.beginPath();
    ctx.moveTo(erx - earW * 0.3, ery);
    ctx.lineTo(erx + p.earRTwitch * earW * 0.5, ery - earH);
    ctx.lineTo(erx + earW * 0.5, ery);
    ctx.fill();
    ctx.fillStyle = "rgba(180,100,100,0.3)";
    ctx.beginPath();
    ctx.moveTo(erx - earW * 0.1, ery - 0.5 * s);
    ctx.lineTo(erx + p.earRTwitch * earW * 0.5, ery - earH + 0.7 * s);
    ctx.lineTo(erx + earW * 0.35, ery - 0.5 * s);
    ctx.fill();

    // Eyes
    if (p.eyeOpen > 0.05) {
      const eyeR = 0.6 * s * p.eyeOpen;
      const eyeY = headY - 0.3 * s;
      // Iris — gold
      ctx.fillStyle = "#d0c040";
      ctx.beginPath();
      ctx.ellipse(headX - 1.3 * s, eyeY, eyeR, eyeR * 0.7, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(headX + 1.5 * s, eyeY - 0.2 * s, eyeR, eyeR * 0.7, 0, 0, Math.PI * 2);
      ctx.fill();
      // Slit pupil
      ctx.fillStyle = "#0a0a0a";
      ctx.beginPath();
      ctx.ellipse(headX - 1.3 * s, eyeY, eyeR * 0.25, eyeR * 0.6, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(headX + 1.5 * s, eyeY - 0.2 * s, eyeR * 0.25, eyeR * 0.6, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // Nose + whiskers (visible when head is lifted enough)
    if (p.headLift > 0.15) {
      const noseY = headY + headR * 0.55;
      ctx.fillStyle = "rgba(200,130,130,0.6)";
      ctx.beginPath();
      ctx.moveTo(headX, noseY - 0.6 * s);
      ctx.lineTo(headX + 0.6 * s, noseY + 0.3 * s);
      ctx.lineTo(headX - 0.6 * s, noseY + 0.3 * s);
      ctx.fill();
      // Whiskers
      ctx.strokeStyle = "rgba(200,200,210,0.2)";
      ctx.lineWidth = 0.3;
      ctx.beginPath();
      ctx.moveTo(headX - 0.8 * s, noseY); ctx.lineTo(headX - 4 * s, noseY - 1.2 * s);
      ctx.moveTo(headX - 0.8 * s, noseY + 0.3 * s); ctx.lineTo(headX - 3.8 * s, noseY + 0.8 * s);
      ctx.moveTo(headX + 0.8 * s, noseY - 0.2 * s); ctx.lineTo(headX + 4 * s, noseY - 1.5 * s);
      ctx.moveTo(headX + 0.8 * s, noseY + 0.2 * s); ctx.lineTo(headX + 3.8 * s, noseY + 0.6 * s);
      ctx.stroke();
    }

    // Yawn mouth
    if (p.jawOpen > 0.1) {
      const mouthY = headY + headR * 0.5;
      const mouthH = p.jawOpen * 2 * s;
      ctx.fillStyle = "#3a1818";
      ctx.beginPath();
      ctx.ellipse(headX, mouthY, 1.3 * s, mouthH, 0, 0, Math.PI * 2);
      ctx.fill();
      if (p.jawOpen > 0.4) {
        ctx.fillStyle = "#c06060";
        ctx.beginPath();
        ctx.ellipse(headX, mouthY + mouthH * 0.3, 0.6 * s, mouthH * 0.35, 0, 0, Math.PI);
        ctx.fill();
      }
    }

    // ── Fire highlight on fur — warm rim on fire-facing side ─────
    const glow = 0.12 + 0.06 * Math.sin(t * 3.5);
    ctx.globalAlpha = glow;
    ctx.fillStyle = "#ff8030";
    ctx.beginPath();
    ctx.ellipse(bodyCx - 3 * s, bodyCy - 1 * s, bodyLen * 0.6, bodyWid * 0.8, spineAng, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  // ── Rug ────────────────────────────────────────────────────────────────────

  private _drawRug(ctx: CanvasRenderingContext2D): void {
    const rx = 1.7, ry = 1.2, rW = 1.8, rH = 1.2;
    const es = this.sc;

    // Base
    this.quad(ctx, rx, ry, 0.012, rx + rW, ry, 0.012, rx + rW, ry + rH, 0.012, rx, ry + rH, 0.012, P.rugBase);

    // Inner field — slightly lighter
    const ip = 0.15;
    this.quad(ctx, rx + ip, ry + ip, 0.013, rx + rW - ip, ry + ip, 0.013,
                   rx + rW - ip, ry + rH - ip, 0.013, rx + ip, ry + rH - ip, 0.013, "#903828");

    const a = this.w2s(rx, ry, 0.012), b = this.w2s(rx + rW, ry, 0.012);
    const c = this.w2s(rx + rW, ry + rH, 0.012), d = this.w2s(rx, ry + rH, 0.012);

    // Outer border
    ctx.strokeStyle = P.rugBorder; ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.lineTo(c.x, c.y); ctx.lineTo(d.x, d.y);
    ctx.closePath(); ctx.stroke();

    // Inner border line
    const ai = this.w2s(rx + ip, ry + ip, 0.013), bi = this.w2s(rx + rW - ip, ry + ip, 0.013);
    const ci = this.w2s(rx + rW - ip, ry + rH - ip, 0.013), di = this.w2s(rx + ip, ry + rH - ip, 0.013);
    ctx.strokeStyle = "rgba(210, 160, 80, 0.4)"; ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(ai.x, ai.y); ctx.lineTo(bi.x, bi.y); ctx.lineTo(ci.x, ci.y); ctx.lineTo(di.x, di.y);
    ctx.closePath(); ctx.stroke();

    const cx = (a.x + c.x) / 2, cy = (a.y + c.y) / 2;
    const sz = Math.abs(b.x - a.x) * 0.22;

    // Central medallion — diamond with inner detail
    ctx.strokeStyle = P.rugInner; ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.moveTo(cx, cy - sz * 0.55); ctx.lineTo(cx + sz, cy);
    ctx.lineTo(cx, cy + sz * 0.55); ctx.lineTo(cx - sz, cy);
    ctx.closePath(); ctx.stroke();

    // Medallion fill — subtle gradient effect
    ctx.fillStyle = "rgba(200, 140, 60, 0.12)";
    ctx.fill();

    // Corner ornaments at each diamond point
    ctx.fillStyle = P.rugInner;
    const pts: [number, number][] = [[cx, cy - sz * 0.55], [cx + sz, cy], [cx, cy + sz * 0.55], [cx - sz, cy]];
    for (const pt of pts) {
      ctx.beginPath(); ctx.arc(pt[0], pt[1], 1.8, 0, Math.PI * 2); ctx.fill();
    }

    // Inner diamond
    const isz = sz * 0.45;
    ctx.strokeStyle = "rgba(200, 150, 80, 0.35)"; ctx.lineWidth = 0.7;
    ctx.beginPath();
    ctx.moveTo(cx, cy - isz * 0.5); ctx.lineTo(cx + isz, cy);
    ctx.lineTo(cx, cy + isz * 0.5); ctx.lineTo(cx - isz, cy);
    ctx.closePath(); ctx.stroke();

    // Central motif — tiny flower/star
    ctx.fillStyle = "rgba(210, 170, 90, 0.5)";
    ctx.beginPath(); ctx.arc(cx, cy, 2, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = "rgba(210, 170, 90, 0.3)"; ctx.lineWidth = 0.5;
    for (let r = 0; r < 4; r++) {
      const ra = (r / 4) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(ra) * isz * 0.35, cy + Math.sin(ra) * isz * 0.35);
      ctx.stroke();
    }

    // Corner scrollwork — small curved ornaments between border and medallion
    ctx.strokeStyle = "rgba(190, 130, 50, 0.22)"; ctx.lineWidth = 0.5;
    // NW corner area
    const nw = { x: (a.x + cx) / 2, y: (a.y + cy) / 2 };
    ctx.beginPath();
    ctx.arc(nw.x, nw.y, sz * 0.2, 0, Math.PI * 1.5);
    ctx.stroke();
    // SE corner area
    const se = { x: (c.x + cx) / 2, y: (c.y + cy) / 2 };
    ctx.beginPath();
    ctx.arc(se.x, se.y, sz * 0.2, Math.PI * 0.5, Math.PI * 2);
    ctx.stroke();

    // Fringe / tassels on two visible edges
    const fringeLen = 3.5;
    ctx.strokeStyle = "rgba(180, 110, 50, 0.30)";
    ctx.lineWidth = 0.5;
    const fringeCount = 16;
    for (let i = 0; i < fringeCount; i++) {
      const f = (i + 0.5) / fringeCount;
      // South edge (c→d)
      const sx = c.x + (d.x - c.x) * f, sy = c.y + (d.y - c.y) * f;
      ctx.beginPath(); ctx.moveTo(sx, sy); ctx.lineTo(sx + 0.4, sy + fringeLen); ctx.stroke();
      // East edge (b→c)
      const ex = b.x + (c.x - b.x) * f, ey = b.y + (c.y - b.y) * f;
      ctx.beginPath(); ctx.moveTo(ex, ey); ctx.lineTo(ex + fringeLen, ey + 0.3); ctx.stroke();
    }
  }

  // ── Table ──────────────────────────────────────────────────────────────────

  private _drawTable(ctx: CanvasRenderingContext2D): void {
    const tx = 3.4, ty = 0.3, tW = 0.85, tD = 0.6, tH = 0.52;
    const es = this.sc;

    // Visible table legs (east and south)
    const legW = 0.06;
    // SE leg
    this.quad(ctx, tx + tW - legW, ty + tD - legW, tH, tx + tW, ty + tD, tH,
                   tx + tW, ty + tD, 0, tx + tW - legW, ty + tD - legW, 0, P.woodMid);
    // NE leg
    this.quad(ctx, tx + tW - legW, ty, tH * 0.1, tx + tW, ty + legW, tH * 0.1,
                   tx + tW, ty + legW, 0, tx + tW - legW, ty, 0, P.woodDark);
    // SW leg (partially visible)
    this.quad(ctx, tx, ty + tD - legW, tH, tx + legW, ty + tD, tH,
                   tx + legW, ty + tD, 0, tx, ty + tD - legW, 0, "#7a5a3a");

    // Tabletop
    this.quad(ctx, tx, ty, tH, tx + tW, ty, tH, tx + tW, ty + tD, tH, tx, ty + tD, tH, P.woodLight);
    // Edge apron — east face
    this.quad(ctx, tx + tW, ty, tH, tx + tW, ty + tD, tH,
                   tx + tW, ty + tD, tH - 0.06, tx + tW, ty, tH - 0.06, P.woodMid);
    // Edge apron — south face
    this.quad(ctx, tx, ty + tD, tH, tx + tW, ty + tD, tH,
                   tx + tW, ty + tD, tH - 0.06, tx, ty + tD, tH - 0.06, "#9a7050");

    // Wood grain lines on top
    if (es > 0.8) {
      ctx.globalAlpha = 0.06;
      ctx.strokeStyle = "#8a6a40";
      ctx.lineWidth = 0.3;
      for (let g = 0; g < 4; g++) {
        const gy = ty + tD * (0.15 + g * 0.22);
        const ga = this.w2s(tx + 0.05, gy, tH + 0.001);
        const gb = this.w2s(tx + tW - 0.05, gy + 0.02, tH + 0.001);
        ctx.beginPath(); ctx.moveTo(ga.x, ga.y); ctx.lineTo(gb.x, gb.y); ctx.stroke();
      }
      ctx.globalAlpha = 1;
    }

    // ── Mug — ceramic with inner liquid visible ──────────────────
    const mugPos = this.w2s(tx + 0.55, ty + 0.25, tH + 0.01);
    // Mug body cylinder
    ctx.fillStyle = "#b8a890";
    ctx.beginPath();
    ctx.ellipse(mugPos.x, mugPos.y + 2.5 * es, 3.5 * es, 2 * es, 0, 0, Math.PI);
    ctx.fill();
    // Mug body side
    ctx.fillStyle = "#c4b8a0";
    ctx.fillRect(mugPos.x - 3.5 * es, mugPos.y - 0.5 * es, 7 * es, 3 * es);
    // Mug rim — top ellipse
    ctx.fillStyle = "#d4c8b0";
    ctx.beginPath();
    ctx.ellipse(mugPos.x, mugPos.y - 0.5 * es, 3.5 * es, 2 * es, 0, 0, Math.PI * 2);
    ctx.fill();
    // Liquid inside — dark brown
    ctx.fillStyle = "#3a2818";
    ctx.beginPath();
    ctx.ellipse(mugPos.x, mugPos.y - 0.3 * es, 2.8 * es, 1.5 * es, 0, 0, Math.PI * 2);
    ctx.fill();
    // Liquid surface highlight
    ctx.fillStyle = "rgba(200, 160, 100, 0.15)";
    ctx.beginPath();
    ctx.ellipse(mugPos.x - 0.5 * es, mugPos.y - 0.5 * es, 1.5 * es, 0.7 * es, -0.3, 0, Math.PI * 2);
    ctx.fill();
    // Handle
    ctx.strokeStyle = "#c4b8a0"; ctx.lineWidth = 1.2;
    ctx.beginPath();
    ctx.arc(mugPos.x + 3.5 * es, mugPos.y + 0.8 * es, 2.2 * es, -1.0, 0.8);
    ctx.stroke();

    // ── Small book next to mug ────────────────────────────────────
    const bookPos = this.w2s(tx + 0.2, ty + 0.35, tH + 0.005);
    ctx.fillStyle = "#6a3a28";
    const bkAngle = -0.5;
    ctx.save();
    ctx.translate(bookPos.x, bookPos.y);
    ctx.rotate(bkAngle);
    ctx.fillRect(-5 * es, -3 * es, 10 * es, 6 * es);
    // Page edges
    ctx.fillStyle = "#e8e0d0";
    ctx.fillRect(-4.5 * es, -2.5 * es, 9 * es, 5 * es);
    // Cover
    ctx.fillStyle = "#6a3a28";
    ctx.fillRect(-5 * es, -3 * es, 10 * es, 0.8 * es);
    ctx.restore();

    // Steam spawn
    if (Math.random() < 0.03) {
      this.steamWisps.push({
        x: mugPos.x, y: mugPos.y - 2 * es,
        age: 0, maxAge: 2 + Math.random() * 2,
        drift: (Math.random() - 0.5) * 4, amp: 2 + Math.random() * 3,
        freq: 0.5 + Math.random() * 0.5, size: 1.5 + Math.random() * 1.5,
      });
    }
  }

  // ── Aeterna ────────────────────────────────────────────────────────────────

  private _drawAeterna(ctx: CanvasRenderingContext2D, t: number): void {
    const dx = 3.6, dy = 0.35, dZ = 0.58, dW = 0.5, dD = 0.35, dH = 0.1;
    const es = this.sc;
    // Device body
    this.quad(ctx, dx, dy, dZ + dH, dx + dW, dy, dZ + dH, dx + dW, dy + dD, dZ + dH, dx, dy + dD, dZ + dH, P.aeternaBody);
    // Thin side edge for depth
    this.quad(ctx, dx + dW, dy, dZ + dH, dx + dW, dy + dD, dZ + dH,
                   dx + dW, dy + dD, dZ, dx + dW, dy, dZ, "#0a0a18");

    const pulse = 0.55 + 0.45 * Math.sin(t * 1.6);
    const tA = this.w2s(dx, dy, dZ + dH), tB = this.w2s(dx + dW, dy, dZ + dH);
    const tC = this.w2s(dx + dW, dy + dD, dZ + dH), tD = this.w2s(dx, dy + dD, dZ + dH);
    const cx = (tA.x + tC.x) / 2, cy = (tA.y + tC.y) / 2;
    const gr = 18 * es;

    ctx.save();
    ctx.beginPath();
    ctx.moveTo(tA.x, tA.y); ctx.lineTo(tB.x, tB.y);
    ctx.lineTo(tC.x, tC.y); ctx.lineTo(tD.x, tD.y);
    ctx.clip();

    // Screen glow
    const sg = ctx.createRadialGradient(cx, cy, 0, cx, cy, gr);
    sg.addColorStop(0,   `rgba(140, 180, 255, ${0.88 * pulse})`);
    sg.addColorStop(0.5, `rgba(80, 120, 220, ${0.45 * pulse})`);
    sg.addColorStop(1,   "rgba(40, 80, 180, 0)");
    ctx.fillStyle = sg;
    ctx.fillRect(cx - gr, cy - gr, gr * 2, gr * 2);

    // Scan lines — subtle horizontal shimmer
    ctx.globalAlpha = 0.08 * pulse;
    ctx.fillStyle = "#a0c0ff";
    const scanY = ((t * 12) % (gr * 2)) - gr;
    ctx.fillRect(cx - gr, cy + scanY, gr * 2, 1.5);
    ctx.fillRect(cx - gr, cy + scanY - gr * 0.6, gr * 2, 0.8);

    // Tiny data dots — constellation-like pattern on the screen
    ctx.globalAlpha = 0.4 * pulse;
    ctx.fillStyle = "#c0d8ff";
    for (let d = 0; d < 5; d++) {
      const dd = d * 1.618;
      const ddx = cx + Math.sin(dd * 2.3 + t * 0.3) * gr * 0.5;
      const ddy = cy + Math.cos(dd * 1.7 + t * 0.2) * gr * 0.35;
      ctx.beginPath(); ctx.arc(ddx, ddy, 0.5, 0, Math.PI * 2); ctx.fill();
    }
    ctx.globalAlpha = 1;
    ctx.restore();

    // Ambient light bleed from screen
    const ag = ctx.createRadialGradient(cx, cy, 0, cx, cy, 35 * es);
    ag.addColorStop(0, `rgba(100, 140, 255, ${0.08 * pulse})`);
    ag.addColorStop(1, "rgba(80, 120, 220, 0)");
    ctx.fillStyle = ag;
    ctx.fillRect(cx - 40 * es, cy - 40 * es, 80 * es, 80 * es);
  }

  // ═════════════════════════════════════════════════════════════════════════════
  //  FIRE LIGHTING — dynamic glow and shadows
  // ═════════════════════════════════════════════════════════════════════════════

  private _drawFireLighting(ctx: CanvasRenderingContext2D, t: number, quality: number): void {
    if (quality < 0.4) return;
    const fk = 0.7 + 0.3 * Math.sin(t * 3.3);
    const es = this.sc;

    // ── Room vignette — darker edges, brighter near fire ────────────
    const rc = this.w2s(2.5, 2, 0);
    const rr = 190 * es;
    const vig = ctx.createRadialGradient(rc.x, rc.y - 25 * es, rr * 0.25, rc.x, rc.y - 25 * es, rr);
    vig.addColorStop(0, "rgba(0,0,0,0)");
    vig.addColorStop(0.5, "rgba(0,0,0,0.05)");
    vig.addColorStop(1, `rgba(0,0,0,${0.35 + 0.08 * (1 - fk)})`);
    ctx.fillStyle = vig;
    ctx.fillRect(rc.x - rr, rc.y - rr - 25 * es, rr * 2, rr * 2);

    // ── Dancing shadow near far wall ────────────────────────────────
    if (quality >= 0.6) {
      const shadePt = this.w2s(0.15, 2.5, 0.3);
      const shadeR = 30 * es;
      ctx.globalAlpha = 0.06 + 0.04 * (1 - fk);
      ctx.fillStyle = "#000";
      ctx.beginPath();
      ctx.ellipse(shadePt.x, shadePt.y, shadeR, shadeR * 1.5, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    // ── Fire caustics — dancing light patches on the floor ──────────
    if (quality >= 0.5) {
      const caustics = [
        { wx: 0.8, wy: 0.7, freq: 2.7, ph: 0, size: 0.7, alpha: 0.065 },
        { wx: 1.1, wy: 0.4, freq: 3.4, ph: 1.3, size: 0.55, alpha: 0.050 },
        { wx: 0.5, wy: 1.0, freq: 2.1, ph: 2.8, size: 0.6, alpha: 0.045 },
        { wx: 1.5, wy: 0.6, freq: 3.8, ph: 0.7, size: 0.45, alpha: 0.035 },
      ];
      for (const c of caustics) {
        const wobX = Math.sin(t * c.freq + c.ph) * 3 * es;
        const wobY = Math.cos(t * c.freq * 0.7 + c.ph + 1) * 2 * es;
        const pt = this.w2s(c.wx, c.wy, 0.01);
        const brightness = c.alpha * fk * (0.7 + 0.3 * Math.sin(t * c.freq * 1.3 + c.ph));
        const cg = ctx.createRadialGradient(
          pt.x + wobX, pt.y + wobY, 0,
          pt.x + wobX, pt.y + wobY, c.size * 16 * es
        );
        cg.addColorStop(0, `rgba(255, 160, 60, ${brightness})`);
        cg.addColorStop(0.5, `rgba(255, 120, 30, ${brightness * 0.4})`);
        cg.addColorStop(1, "rgba(200, 80, 0, 0)");
        ctx.fillStyle = cg;
        ctx.beginPath();
        // Elliptical caustic — wobbles in shape
        const rx = c.size * 16 * es * (0.8 + 0.2 * Math.sin(t * c.freq * 0.5));
        const ry = c.size * 10 * es * (0.8 + 0.2 * Math.cos(t * c.freq * 0.5));
        ctx.ellipse(pt.x + wobX, pt.y + wobY, rx, ry, -0.45, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // ── Rim lighting — warm edges on furniture facing the fire ───────
    if (quality >= 0.5) {
      const rimAlpha = 0.09 * fk;

      // Armchair fire-facing edge
      const chairRim = this.w2s(1.3, 1.6, 0.35);
      ctx.strokeStyle = `rgba(255, 140, 50, ${rimAlpha})`;
      ctx.lineWidth = 1.5 * es;
      ctx.beginPath();
      ctx.moveTo(chairRim.x - 2 * es, chairRim.y + 12 * es);
      ctx.quadraticCurveTo(chairRim.x - 6 * es, chairRim.y, chairRim.x - 2 * es, chairRim.y - 10 * es);
      ctx.stroke();

      // Table fire-facing edge
      const tableRim = this.w2s(3.4, 0.3, 0.52);
      ctx.lineWidth = 1 * es;
      ctx.beginPath();
      ctx.moveTo(tableRim.x - 8 * es, tableRim.y + 3 * es);
      ctx.lineTo(tableRim.x - 8 * es, tableRim.y + 14 * es);
      ctx.stroke();

      // Bookcase fire-facing edge
      const bookRim = this.w2s(0.22, 0.7, 0.75);
      ctx.lineWidth = 0.8 * es;
      ctx.beginPath();
      ctx.moveTo(bookRim.x + 1 * es, bookRim.y);
      ctx.lineTo(bookRim.x + 1 * es, bookRim.y + 20 * es);
      ctx.stroke();

      // Rug shimmer near fire
      const rugShimmer = this.w2s(1.9, 1.3, 0.013);
      ctx.globalAlpha = rimAlpha * 0.6;
      ctx.fillStyle = "#ff9030";
      ctx.beginPath();
      ctx.ellipse(rugShimmer.x, rugShimmer.y, 18 * es, 8 * es, -0.45, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }
  }

  // ═════════════════════════════════════════════════════════════════════════════
  //  LAYER 3 — PARTICLES
  // ═════════════════════════════════════════════════════════════════════════════

  private _drawParticles(ctx: CanvasRenderingContext2D, t: number, W: number, H: number, quality: number, roomReveal: number): void {
    if (quality < 0.4) return;
    const dt = 1 / 60;
    const roomTopY = this.oy - 40 * this.sc;

    // ── Embers (orange → white as they rise into cosmos) ────────────────
    for (let i = this.embers.length - 1; i >= 0; i--) {
      const e = this.embers[i];
      e.x += e.vx * dt;
      e.y += e.vy * dt;
      e.vx += (Math.random() - 0.5) * 5 * dt;
      e.vy += 4 * dt;
      e.life -= dt;

      if (e.life <= 0 || this.embers.length > 100) { this.embers.splice(i, 1); continue; }

      const alpha = Math.max(0, e.life / e.max) * Math.max(0.3, roomReveal);
      ctx.globalAlpha = alpha * 0.85;

      if (e.y < roomTopY) {
        const blend = Math.min(1, (roomTopY - e.y) / (120 * this.sc));
        const sat = 100 * (1 - blend * 0.85);
        const lit = 65 + blend * 28;
        ctx.fillStyle = `hsl(${e.hue + blend * 30}, ${sat}%, ${lit}%)`;
        ctx.beginPath();
        ctx.arc(e.x, e.y, Math.max(0.3, e.s * alpha * (1 - blend * 0.4)), 0, Math.PI * 2);
      } else {
        ctx.fillStyle = `hsl(${e.hue}, 100%, 65%)`;
        ctx.beginPath();
        ctx.arc(e.x, e.y, Math.max(0.3, e.s * alpha), 0, Math.PI * 2);
      }
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // ── Smoke wisps from chimney ────────────────────────────────────────
    if (quality >= 0.5 && Math.random() < 0.03 * Math.max(0.2, roomReveal)) {
      const chimney = this.w2s(0.8, 0, 2.4);
      this.smokeWisps.push({
        x: chimney.x + (Math.random() - 0.5) * 6,
        y: chimney.y,
        age: 0, maxAge: 5 + Math.random() * 5,
        drift: (Math.random() - 0.2) * 7,
        amp: 6 + Math.random() * 12,
        freq: 0.25 + Math.random() * 0.35,
        size: 3 + Math.random() * 5,
      });
    }

    for (let i = this.smokeWisps.length - 1; i >= 0; i--) {
      const w = this.smokeWisps[i];
      w.age += dt;
      if (w.age >= w.maxAge || this.smokeWisps.length > 15) { this.smokeWisps.splice(i, 1); continue; }

      const progress = w.age / w.maxAge;
      w.y -= 14 * dt;
      const wx = w.x + w.drift * w.age + Math.sin(w.age * w.freq) * w.amp * progress;
      const alpha = Math.sin(progress * Math.PI) * 0.05 * Math.max(0.2, roomReveal);
      const radius = w.size * (0.5 + progress * 2);

      ctx.globalAlpha = alpha;
      ctx.fillStyle = "#8890a8";
      ctx.beginPath();
      ctx.arc(wx, w.y, radius, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // ── Mug steam ───────────────────────────────────────────────────────
    for (let i = this.steamWisps.length - 1; i >= 0; i--) {
      const w = this.steamWisps[i];
      w.age += dt;
      if (w.age >= w.maxAge || this.steamWisps.length > 8) { this.steamWisps.splice(i, 1); continue; }

      const progress = w.age / w.maxAge;
      w.y -= 8 * dt;
      const wx = w.x + Math.sin(w.age * w.freq) * w.amp;
      const alpha = Math.sin(progress * Math.PI) * 0.08 * Math.max(0.2, roomReveal);

      ctx.globalAlpha = alpha;
      ctx.fillStyle = "#c0c8d8";
      ctx.beginPath();
      ctx.arc(wx, w.y, w.size * (0.4 + progress), 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // ── Dust motes in moonbeam ──────────────────────────────────────────
    if (quality >= 0.6 && Math.random() < 0.06 * Math.max(0.2, roomReveal)) {
      const winCenter = this.w2s(3.1, 1.3, 0.7);
      this.dustMotes.push({
        x: winCenter.x + (Math.random() - 0.5) * 40 * this.sc,
        y: winCenter.y + (Math.random() - 0.5) * 50 * this.sc,
        vx: (Math.random() - 0.5) * 2.5, vy: (Math.random() - 0.52) * 1.5,
        life: 4 + Math.random() * 5, max: 0, s: 0.4 + Math.random() * 0.8,
      });
      this.dustMotes[this.dustMotes.length - 1].max = this.dustMotes[this.dustMotes.length - 1].life;
    }

    for (let i = this.dustMotes.length - 1; i >= 0; i--) {
      const m = this.dustMotes[i];
      m.x += m.vx * dt + Math.sin(t * 0.6 + i * 1.3) * 0.25 * dt;
      m.y += m.vy * dt;
      m.life -= dt;
      if (m.life <= 0 || this.dustMotes.length > 35) { this.dustMotes.splice(i, 1); continue; }

      const alpha = Math.sin((m.life / m.max) * Math.PI) * 0.28 * Math.max(0.2, roomReveal);
      ctx.globalAlpha = alpha;
      ctx.fillStyle = "#b8c8e0";
      ctx.beginPath();
      ctx.arc(m.x, m.y, m.s, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;

    // ── Spark shower particles ────────────────────────────────────────
    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const s = this.sparks[i];
      s.x += s.vx * dt;
      s.y += s.vy * dt;
      s.vy += 60 * dt; // gravity
      s.vx *= 0.995;
      s.life -= dt;
      if (s.life <= 0) { this.sparks.splice(i, 1); continue; }

      const frac = s.life / s.max;
      ctx.globalAlpha = frac * 0.9 * Math.max(0.3, roomReveal);
      // Bright orange → red → dim
      const hue = 20 + (1 - frac) * 15;
      const lit = 70 - (1 - frac) * 30;
      ctx.fillStyle = `hsl(${hue}, 100%, ${lit}%)`;
      ctx.beginPath();
      ctx.arc(s.x, s.y, Math.max(0.3, s.s * frac), 0, Math.PI * 2);
      ctx.fill();

      // Tiny trail
      if (frac > 0.3) {
        ctx.globalAlpha = frac * 0.3;
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(s.x - s.vx * dt * 3, s.y - s.vy * dt * 3);
        ctx.strokeStyle = `hsl(${hue}, 80%, ${lit + 10}%)`;
        ctx.lineWidth = Math.max(0.2, s.s * frac * 0.5);
        ctx.stroke();
      }
    }
    ctx.globalAlpha = 1;

    // ── Fire-lit dust — tiny motes catching firelight near the hearth ──
    if (quality >= 0.6 && roomReveal > 0.5) {
      const fk = 0.7 + 0.3 * Math.sin(t * 3.3);
      for (let i = 0; i < 6; i++) {
        const seed = i * 3.91 + 0.73;
        const rawT = ((seed * 0.23 + t * 0.04) % 1);
        const fdx = this.ox + ((Math.sin(seed * 5.1) * 0.4) - 0.1) * 60 * this.sc;
        const fdy = this.oy + ((Math.sin(seed * 3.7) * 0.3) + 0.15) * 60 * this.sc;
        const fdAlpha = Math.sin(rawT * Math.PI) * 0.22 * fk * roomReveal;
        const drift = Math.sin(t * 0.4 + seed * 2) * 3;

        ctx.globalAlpha = fdAlpha;
        ctx.fillStyle = "#ffc860";
        ctx.beginPath();
        ctx.arc(fdx + drift, fdy, 0.5 + Math.sin(seed) * 0.3, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    }

    // ── Golden motes ────────────────────────────────────────────────────
    for (let i = 0; i < 12; i++) {
      const seed = i * 7.31 + 1.17;
      const mx = W * (0.10 + 0.80 * ((Math.sin(seed * 3.7) + 1) / 2));
      const rawY = ((seed * 0.37 + t * 0.010 * (0.6 + (i % 3) * 0.25)) % 1);
      const my = H * (1.10 - rawY);
      const mAlpha = Math.sin(rawY * Math.PI) * 0.22;
      const drift = Math.sin(t * 0.3 + seed) * 14;

      ctx.globalAlpha = mAlpha;
      ctx.fillStyle = "#e8c080";
      ctx.beginPath();
      ctx.arc(mx + drift, my, 0.8 + Math.sin(seed) * 0.5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.globalAlpha = 1;
  }

  // ═════════════════════════════════════════════════════════════════════════════
  //  EASTER EGGS — rewards for patience
  // ═════════════════════════════════════════════════════════════════════════════

  private _checkEasterEggs(t: number): void {
    // ~30s: Cat wakes up and grooms
    if (t > 30 && !this.easterEggsTriggered.has("catStretch")) {
      this.easterEggsTriggered.add("catStretch");
      this.catAnim.state = "groom";
      this.catAnim.stateT = 0;
      this.catAnim.nextChange = 4.0;
      Object.assign(this.catAnim.target, TitleRenderer.CAT_ZERO_POSE, this._catTargetForState("groom", t));
    }

    // ~55s: Grand shooting star (handled in _drawCosmos via flag)
    if (t > 55 && !this.easterEggsTriggered.has("grandStar")) {
      this.easterEggsTriggered.add("grandStar");
      this.grandShootingStar = true;
    }
    if (this.grandShootingStar && t > 58) {
      this.grandShootingStar = false;
    }

    // ~75-105s: Spark shower from fire
    if (t > this.nextSparkBurst && !this.easterEggsTriggered.has("spark1")) {
      this.easterEggsTriggered.add("spark1");
      this._triggerSparkBurst();
      this.nextSparkBurst = t + 60 + Math.random() * 40;
    }

    // Second spark burst
    if (t > this.nextSparkBurst && this.easterEggsTriggered.has("spark1") && !this.easterEggsTriggered.has("spark2")) {
      this.easterEggsTriggered.add("spark2");
      this._triggerSparkBurst();
    }

    // ~90s: Cat yawn
    if (t > 90 && !this.easterEggsTriggered.has("catYawn")) {
      this.easterEggsTriggered.add("catYawn");
      this.catAnim.state = "yawn";
      this.catAnim.stateT = 0;
      this.catAnim.nextChange = 3.0;
      Object.assign(this.catAnim.target, TitleRenderer.CAT_ZERO_POSE, this._catTargetForState("yawn", t));
    }

    // ~120s: Cat gets up and walks
    if (t > 120 && !this.easterEggsTriggered.has("catWalk")) {
      this.easterEggsTriggered.add("catWalk");
      this.catAnim.state = "walk";
      this.catAnim.stateT = 0;
      this.catAnim.nextChange = 5.0;
      this.catAnim.walkDir = 1;
      Object.assign(this.catAnim.target, TitleRenderer.CAT_ZERO_POSE, this._catTargetForState("walk", t));
    }

    // ~180s: Moth appears near candle
    if (t > 180 && !this.easterEggsTriggered.has("moth")) {
      this.easterEggsTriggered.add("moth");
      this.moth.active = true;
    }

    // ~600s: Aurora surge — sky briefly blazes with color
    if (t > 600 && !this.easterEggsTriggered.has("auroraSurge")) {
      this.easterEggsTriggered.add("auroraSurge");
      this.auroraBoost = 1;
    }
    // Aurora boost decay
    if (this.auroraBoost > 0) {
      this.auroraBoost = Math.max(0, this.auroraBoost - 1 / 60 / 8); // 8s fade
    }
  }

  private _triggerSparkBurst(): void {
    const fireScreen = this.w2s(0.8, 0.05, 0.6);
    for (let i = 0; i < 18; i++) {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * 1.4;
      const speed = 40 + Math.random() * 80;
      this.sparks.push({
        x: fireScreen.x + (Math.random() - 0.5) * 8,
        y: fireScreen.y,
        vx: Math.cos(angle) * speed * (0.5 + Math.random()),
        vy: Math.sin(angle) * speed,
        life: 0.6 + Math.random() * 1.2,
        max: 0,
        s: 0.5 + Math.random() * 1.2,
      });
      this.sparks[this.sparks.length - 1].max = this.sparks[this.sparks.length - 1].life;
    }
  }

  // ═════════════════════════════════════════════════════════════════════════════
  //  MOTH — tiny creature orbiting the candle
  // ═════════════════════════════════════════════════════════════════════════════

  private _drawMoth(ctx: CanvasRenderingContext2D, t: number): void {
    const m = this.moth;
    if (!m.active) return;
    const es = this.sc;

    // Candle top position (from fireplace constants)
    const fx = 0.2, fw = 1.2, fh = 1.1, depth = 0.2;
    const candleX = fx + fw * 0.75;
    const candleTop = this.w2s(candleX, depth * 0.5, fh + 0.15);

    // Erratic orbital motion — figure-8 ish
    m.angle += (1 / 60) * (2.2 + Math.sin(t * 1.7) * 0.8);
    m.radius = (10 + Math.sin(t * 0.9) * 4 + Math.sin(t * 2.3) * 2) * es;
    m.wingPhase += (1 / 60) * 25;

    const mx = candleTop.x + Math.cos(m.angle) * m.radius;
    const my = candleTop.y - 3 * es + Math.sin(m.angle * 1.7) * m.radius * 0.6;

    // Tiny body
    ctx.fillStyle = "#8a7a6a";
    ctx.beginPath();
    ctx.ellipse(mx, my, 0.8 * es, 0.4 * es, m.angle, 0, Math.PI * 2);
    ctx.fill();

    // Wings — V-shape that flaps
    const wingOpen = Math.sin(m.wingPhase) * 0.5 + 0.5; // 0-1
    const wingSpan = (2 + wingOpen * 1.5) * es;
    const wingAngle = m.angle + Math.PI / 2;
    ctx.globalAlpha = 0.45;
    ctx.fillStyle = "#c8b8a8";
    // Left wing
    ctx.beginPath();
    ctx.moveTo(mx, my);
    ctx.lineTo(
      mx + Math.cos(wingAngle - 0.4 - wingOpen * 0.3) * wingSpan,
      my + Math.sin(wingAngle - 0.4 - wingOpen * 0.3) * wingSpan
    );
    ctx.lineTo(
      mx + Math.cos(wingAngle - 0.1) * wingSpan * 0.6,
      my + Math.sin(wingAngle - 0.1) * wingSpan * 0.6
    );
    ctx.fill();
    // Right wing
    ctx.beginPath();
    ctx.moveTo(mx, my);
    ctx.lineTo(
      mx + Math.cos(wingAngle + 0.4 + wingOpen * 0.3) * wingSpan,
      my + Math.sin(wingAngle + 0.4 + wingOpen * 0.3) * wingSpan
    );
    ctx.lineTo(
      mx + Math.cos(wingAngle + 0.1) * wingSpan * 0.6,
      my + Math.sin(wingAngle + 0.1) * wingSpan * 0.6
    );
    ctx.fill();
    ctx.globalAlpha = 1;

    // Tiny shadow on candle when passing in front
    if (Math.cos(m.angle) > 0.3) {
      ctx.globalAlpha = 0.08;
      ctx.fillStyle = "#000";
      ctx.beginPath();
      ctx.ellipse(candleTop.x + 1 * es, candleTop.y - 1 * es, 2 * es, 1 * es, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
    }
  }

  // ═════════════════════════════════════════════════════════════════════════════
  //  LAYER 4 — TEXT OVERLAY
  // ═════════════════════════════════════════════════════════════════════════════

  private _drawOverlay(
    ctx: CanvasRenderingContext2D, t: number, W: number, H: number,
    tCosmos: number, tTitle: number, tSub: number, tPrompt: number,
  ): void {
    // Fade from black
    if (tCosmos < 1) {
      ctx.fillStyle = `rgba(3, 3, 8, ${1 - tCosmos})`;
      ctx.fillRect(0, 0, W, H);
    }

    // Scene breathing
    const breathe = 0.02 * Math.sin(t * 0.7);
    if (breathe > 0) {
      ctx.fillStyle = `rgba(200, 130, 50, ${breathe})`;
      ctx.fillRect(0, 0, W, H);
    }

    // Top gradient for title legibility
    const topG = ctx.createLinearGradient(0, 0, 0, H * 0.28);
    topG.addColorStop(0, "rgba(3, 3, 8, 0.88)");
    topG.addColorStop(1, "rgba(3, 3, 8, 0)");
    ctx.fillStyle = topG;
    ctx.fillRect(0, 0, W, H * 0.28);

    // Bottom gradient for prompt legibility
    const botG = ctx.createLinearGradient(0, H * 0.84, 0, H);
    botG.addColorStop(0, "rgba(3, 3, 8, 0)");
    botG.addColorStop(1, "rgba(3, 3, 8, 0.65)");
    ctx.fillStyle = botG;
    ctx.fillRect(0, H * 0.84, W, H * 0.16);

    // Full-scene vignette
    const vr = Math.max(W, H) * 0.72;
    const vig = ctx.createRadialGradient(W / 2, H * 0.48, vr * 0.3, W / 2, H * 0.48, vr);
    vig.addColorStop(0, "rgba(0,0,0,0)");
    vig.addColorStop(1, "rgba(0,0,0,0.55)");
    ctx.fillStyle = vig;
    ctx.fillRect(0, 0, W, H);

    // ── Flame emblem — the logo mark ──────────────────────────────────
    if (tTitle > 0) {
      const titleSize = Math.min(92, W * 0.095);
      const emblemY = H * 0.10 - titleSize * 0.55;
      const emblemX = W / 2;
      const es = titleSize * 0.012; // scale factor relative to title size
      const flicker = 0.85 + 0.15 * Math.sin(t * 5.5);
      const sway = Math.sin(t * 2.3) * es * 1.2;

      ctx.save();
      ctx.globalAlpha = tTitle * flicker;
      ctx.translate(emblemX + sway, emblemY);

      // Outer glow
      const glowR = 28 * es;
      const glowG = ctx.createRadialGradient(0, 4 * es, 0, 0, 0, glowR);
      glowG.addColorStop(0, `rgba(255, 140, 50, ${0.18 * flicker})`);
      glowG.addColorStop(0.5, `rgba(200, 80, 20, ${0.06 * flicker})`);
      glowG.addColorStop(1, "rgba(150, 40, 0, 0)");
      ctx.fillStyle = glowG;
      ctx.fillRect(-glowR, -glowR + 4 * es, glowR * 2, glowR * 2);

      // Outer flame — bezier teardrop
      const outerH = 22 * es * flicker;
      const outerW = 9 * es;
      const outerG = ctx.createLinearGradient(0, 8 * es, 0, -outerH);
      outerG.addColorStop(0,   "rgba(200, 60, 15, 0.9)");
      outerG.addColorStop(0.3, "rgba(230, 110, 30, 0.85)");
      outerG.addColorStop(0.6, "rgba(245, 170, 60, 0.8)");
      outerG.addColorStop(1,   "rgba(255, 230, 130, 0.6)");
      ctx.fillStyle = outerG;
      ctx.beginPath();
      ctx.moveTo(0, 8 * es);
      ctx.bezierCurveTo(-outerW, -outerH * 0.2, -outerW * 0.6, -outerH * 0.7, sway * 0.5, -outerH);
      ctx.bezierCurveTo(outerW * 0.6, -outerH * 0.7, outerW, -outerH * 0.2, 0, 8 * es);
      ctx.fill();

      // Inner flame
      const innerH = 15 * es * flicker;
      const innerW = 5 * es;
      const innerG = ctx.createLinearGradient(0, 5 * es, 0, -innerH);
      innerG.addColorStop(0,   "rgba(245, 170, 60, 0.9)");
      innerG.addColorStop(0.4, "rgba(255, 220, 100, 0.85)");
      innerG.addColorStop(1,   "rgba(255, 248, 220, 0.7)");
      ctx.fillStyle = innerG;
      ctx.beginPath();
      ctx.moveTo(0, 5 * es);
      ctx.bezierCurveTo(-innerW, -innerH * 0.1, -innerW * 0.5, -innerH * 0.65, sway * 0.3, -innerH);
      ctx.bezierCurveTo(innerW * 0.5, -innerH * 0.65, innerW, -innerH * 0.1, 0, 5 * es);
      ctx.fill();

      // Core — bright white-gold
      const coreH = 8 * es * flicker;
      ctx.fillStyle = `rgba(255, 250, 235, ${0.7 * flicker})`;
      ctx.beginPath();
      ctx.moveTo(0, 3 * es);
      ctx.bezierCurveTo(-2.5 * es, -coreH * 0.1, -1.5 * es, -coreH * 0.6, 0, -coreH);
      ctx.bezierCurveTo(1.5 * es, -coreH * 0.6, 2.5 * es, -coreH * 0.1, 0, 3 * es);
      ctx.fill();

      ctx.restore();
    }

    // ── "Hearth" — letter by letter ─────────────────────────────────────
    if (tTitle > 0) {
      const titleSize = Math.min(92, W * 0.095);
      const titleY = H * 0.10;
      const word = "Hearth";

      ctx.font = `bold ${titleSize}px 'Courier New', Courier, monospace`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      const totalW = ctx.measureText(word).width;
      const startX = W / 2 - totalW / 2;

      for (let i = 0; i < word.length; i++) {
        const lp = Math.max(0, Math.min(1, (tTitle * word.length - i) * 1.2));
        if (lp <= 0) continue;

        const char = word[i];
        const preW = ctx.measureText(word.substring(0, i)).width;
        const cx = startX + preW + ctx.measureText(char).width / 2;
        const rise = (1 - lp) * 12;
        const ly = titleY + rise;

        ctx.save();
        ctx.globalAlpha = lp;

        // Outer glow
        ctx.shadowColor = "#d48040";
        ctx.shadowBlur = 40 * lp;
        ctx.fillStyle = "#dca060";
        ctx.fillText(char, cx, ly);

        // Mid glow
        ctx.shadowColor = "#ffaa50";
        ctx.shadowBlur = 16 * lp;
        ctx.fillStyle = "#e8c080";
        ctx.fillText(char, cx, ly);

        // Inner text
        ctx.shadowBlur = 0;
        ctx.fillStyle = P.titleWarm;
        ctx.fillText(char, cx, ly);

        ctx.restore();

        // Arrival spark
        if (lp > 0.1 && lp < 0.6) {
          const sparkAlpha = Math.sin((lp - 0.1) / 0.5 * Math.PI) * 0.8;
          ctx.globalAlpha = sparkAlpha;
          ctx.fillStyle = "#fff8e0";
          ctx.beginPath();
          ctx.arc(cx, ly - titleSize * 0.35, 2 + lp * 3, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
        }
      }

      // Golden underline
      const lineW = totalW * tTitle * 0.88;
      const lineY = titleY + titleSize * 0.52;
      const la = tTitle * 0.28;
      const lg = ctx.createLinearGradient(W / 2 - lineW / 2, 0, W / 2 + lineW / 2, 0);
      lg.addColorStop(0, "rgba(200,150,80,0)");
      lg.addColorStop(0.12, `rgba(200,150,80,${la})`);
      lg.addColorStop(0.5, `rgba(230,195,125,${la * 1.5})`);
      lg.addColorStop(0.88, `rgba(200,150,80,${la})`);
      lg.addColorStop(1, "rgba(200,150,80,0)");
      ctx.fillStyle = lg;
      ctx.fillRect(W / 2 - lineW / 2, lineY, lineW, 1.5);
    }

    // ── Subtitle ────────────────────────────────────────────────────────
    if (tSub > 0) {
      const titleSize = Math.min(92, W * 0.095);
      const subY = H * 0.10 + titleSize * 0.70;
      const subSize = Math.min(15, W * 0.018);

      ctx.save();
      ctx.globalAlpha = tSub * 0.75;
      ctx.font = `${subSize}px 'Courier New', Courier, monospace`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = P.sub;
      ctx.fillText("the place where digital souls gather", W / 2, subY);
      ctx.restore();
    }

    // ── Prompt ──────────────────────────────────────────────────────────
    if (tPrompt > 0) {
      const py = H * 0.93;
      const pulse = 0.40 + 0.60 * Math.sin(t * 1.3);
      const pa = tPrompt * pulse;
      const ps = Math.min(13, W * 0.015);

      ctx.save();
      ctx.globalAlpha = pa;
      ctx.font = `${ps}px 'Courier New', Courier, monospace`;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = P.prompt;
      ctx.fillText("click anywhere to enter", W / 2, py);

      const pw = ctx.measureText("click anywhere to enter").width;
      const dd = pw / 2 + 18;
      ctx.globalAlpha = pa * 0.45;
      ctx.fillStyle = "#c8a060";
      ctx.beginPath(); ctx.arc(W / 2 - dd, py, 1.5, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(W / 2 + dd, py, 1.5, 0, Math.PI * 2); ctx.fill();

      ctx.fillStyle = `rgba(200,160,100,${pa * 0.12})`;
      ctx.fillRect(W / 2 - dd - 40, py - 0.4, 32, 0.8);
      ctx.fillRect(W / 2 + dd + 8, py - 0.4, 32, 0.8);
      ctx.restore();
    }
  }

  // ═════════════════════════════════════════════════════════════════════════════
  //  CLEANUP
  // ═════════════════════════════════════════════════════════════════════════════

  destroy(): void {
    this.canvas.removeEventListener("mousemove", this._onMouse);
    this.embers.length = 0;
    this.dustMotes.length = 0;
    this.smokeWisps.length = 0;
    this.steamWisps.length = 0;
    this.sparks.length = 0;
    this.easterEggsTriggered.clear();
    this.moth.active = false;
  }
}
